// ═══════════════════════════════════════════════════════════════════
// Shatterfield3D.js — SHIPS 1–7
//   1 GREY CARDS · 2 THE TABLE SET RIGHT · 3 MOTION
//   4 REAL FACES · 5 THE MATERIAL LANGUAGE
//   6 THE CHROME AND THE THUNDER · 7 THE ATMOSPHERE
//   7.5 THE COMPANION
// ═══════════════════════════════════════════════════════════════════
// The 3D renderer for the Shatterfield battle field.
//
// THE CONTRACT (non-negotiable):
//   • Pure renderer. Same data in (me / opponent), same callbacks out
//     as the two 2D <PlayerSide>s. ZERO game logic. ZERO Firebase.
//   • Rollback = flip the field3D toggle in Shatterfield.js. The game
//     cannot be broken from inside this file.
//   • All radials / modals / toasts stay DOM in Shatterfield.js and
//     float over this Canvas untouched (Layer 7 — chrome unchanged).
//   • SHIP 6 adds ONE more Layer-7 citizen of its own — <FieldChrome3D>,
//     the duel HUD — rendered OUTSIDE the Canvas at z 12, strictly under
//     Shatterfield.js's own chrome. It is pure DOM and pure presentation:
//     it reads `me` / `opponent` and fires the two draw callbacks it was
//     handed. It cannot break the duel either.
//
// PALETTE LAW: SD = field / cards / table. GM (theme.js) = Html chrome.
// PERF LAW (ie-realtime-perf): no setState in useFrame — all animation
// is imperative on refs. Textures cached module-level by key.
// PRNG LAW: mulberry32 seeded by ID — never Math.random() in a render
// path. Necropolis scatter is identical for every client, forever.
// ═══════════════════════════════════════════════════════════════════
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal as domPortal } from "react-dom";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
// ⬡ CSS3D — three's own DOM-in-world renderer. Ships inside the three
// package (zero new deps). This replaces drei's <Html transform>, which
// silently mounts NOTHING on caP'n's machine (the July 24 failure, and
// again July 25 — plain <Html> billboards paint fine, transform mode is
// the dead freedom). CSS3DRenderer is the battle-tested path: it drives
// a plain absolutely-positioned overlay div with matrix3d transforms,
// synced to the same camera every frame.
import { CSS3DRenderer, CSS3DObject } from "three/examples/jsm/renderers/CSS3DRenderer.js";
import {
  ESCENDANTS, TOMES as TOME_CARDS, IMPERA, UHLTIS,
  AFFINITIES, PROPHECIES, CURIOS, ESCLINGS,
} from "./cards";
import { SD, GM } from "./theme";
import { StatGlyph } from "./StatGlyph";
import { CARD, TABLE, Y, SEAT, CAM } from "./fieldLayout3D";
// THE BATTLE LINE IS FOUR — rules.js owns the width of the Escling row and
// is the single source of truth for it. The renderer must never restate that
// number as a literal: a hardcoded row width silently disagrees with the
// engine the next time the line changes, and the board quietly stops drawing
// the rifts the rules say exist.
import { ESCLING_SLOTS } from "./rules";
// ⬡ SHIP 5 — rarity made physical. Thickness, milled edge, and one
// bespoke shine motion per holo tier. Recipes live in ONE file; adding
// a tenth tier costs zero lines here.
import {
  holoThickness, holoEdgeMaterial, hasFoil3D, PLAIN_EDGE,
  FoilPlane, useFoilRim,
} from "./holoMaterial3D";
// ⬡ THE ONE FACE (caP'n's ruling, July 24) — the 3D board does not paint
// its own card. It mounts the SAME <CardFace> the booster, Codex, Vault,
// Deck Builder and 2D duel render — same slab, same art, same scrim, same
// text, same <HoloFoil>. One component, every surface. The procedural
// faceTexture below survives ONLY as the loading slab under the DOM and
// as the automatic fallback if the DOM layer ever fails to paint.
import CardFace, { PrimeraStamp } from "./CardRenderer";
// ⬡ SHIP 8 — THE BODIES (2026-09-15). caP'n: "I want the Shatterfield to
// have the character Models from Annihilation, let's see Spectral Tiger
// Beb on the field when it is summoned! … true 3D battling." Every
// Escendant on a rift now STANDS: the kit body (ieBeingModels.jsx,
// vendored byte-identical, never edited here) on top of its own card.
// fieldBeings3D.jsx is Terror's adapter — the pose, the fit, the stand.
// Pure renderer, same as everything else in this file: delete the two
// lines that mount it and the rift is the card slab it was in Ship 7.
import {
  FieldBeing, beingOf, FIELD_BEING_KIT_VERSION,
  // SHIP 8.2 — THE LIFE. The bus the detector writes entrance kinds to,
  // the one-shot ghost (an Ascend's shed form, a ruined being's streak
  // into the Necropolis), and the seat stances for a world-space yaw.
  beingLife, GhostBody, STANCE,
} from "./fieldBeings3D";
// ⬡ SHIP 8.4 — THE FURNITURE. Treasures, Constructions and Rites as
// objects standing on their Tome cards; the Necropolis as a grave-court
// around its pile; the Exile pit the 3D board never had. Pure props —
// same rider/noDomFace path the beings use, nothing new in the gate.
import { FieldProp, hasProp, CellarDoor, ExilePit, DeathMark } from "./fieldProps3D";
// ⬡ SHIP 6 — THE CHROME. The 2D duel HUD (Anima heart, Khao vortex,
// name plates, framed timers, LUX/GRIM tiles) floated over the board as
// plain DOM. Pure presentation, zero game logic, rendered OUTSIDE the
// Canvas at Layer 7 — Shatterfield.js's radials/modals/heralds still win.
import FieldChrome3D from "./FieldChrome3D";
// ⬡ SHIP 7 — THE ATMOSPHERE. Anima mist, Khao rivers, the two wells,
// the lit seam and the aenergy horizon. Pure renderer, zero logic; one
// module-level bus in, nothing out. Delete this one line and the board
// falls back to exactly what Ship 6 shipped.
// `surge` is the atmosphere's H24 door: a score may wash the whole
// field in its own colour. One writer, a timestamp, readers that decay
// off it — the same shape ATMOS.strikeAt has used since Ship 7.
import FieldAtmos3D, { surge } from "./FieldAtmos3D";
// ⬡ SHIP 7.5 — YOUR COMPANION. The ONE FACE COROLLARY applied to a
// second thing: the 3D board does not paint its own companion, it
// mounts the SAME <CompanionHUD> the 2D duel mounts. Extracted out of
// Shatterfield.js so both boards can import one component.
import CompanionHUD from "./CompanionHUD";
// ⬡ H21 — THE STRIKE LANGUAGE (July 26). caP'n: "3D only." ONE registry
// maps type / Tome school / Ilk → motion profile, so a new card costs
// zero animation code here. Pure math, no CSS and no DOM: every profile
// is a sampler this file feeds `dt` and writes onto a ref, which is the
// only shape the PERF LAW allows. T17's dial (FULL / QUICK / OFF) lives
// in the same file — an animation system without its off-switch is a
// feature you can't undo.
import {
  useMotion, motionOn, motionRate, scaleMs,
  arrivalFor, strikeFor, impactFor, strikePose,
  MOTION_ORDER, MOTION_LABELS, getMotion, setMotion,
  holdFor, subscribeMotionFire,
} from "./strikeLanguage";
// ⬡ H23 — THE BESTIARY OF MOTION (July 27). caP'n: "read every single
// card and truly think about them... let's get super unique animations
// for every single card and interaction." H21's taxonomy is now the
// FLOOR; these two files are the ceiling coming off. `fx3D` is the
// thirteen-primitive vocabulary every animation is played on;
// `cardMotion` is what each card actually DOES, written as data. An
// unauthored card resolves to null here and keeps H21's profile
// unchanged — there is no half-finished state in this system.
import { ScoreLayer, CUE_CAP } from "./fx3D";
import {
  scoreFor, resolveScore, scoreHoldMs, CHANNELS,
  // H24 — the board's own verbs. These are the events that are not a
  // CARD: a rung climbed, a being fractured, a zone emptied, a chair
  // changed. The detector could already see every one of them and had
  // nothing to say about any of them.
  boardScore, ladderScore, animaScore, climbInfo,
} from "./cardMotion";
// ⬡ THE SEAL — a ruined being carries an identity tag for the rest of the
// Solrend (necroSeal.js). The renderer must strip it before it looks a
// card up or asks the CDN for its art, or a Resurrected being becomes a
// faceless slab. cardUrl does its own stripping; baseId is here for the
// lookup and the art cache key.
import { baseId } from "./necroSeal";
// THE FAIL-SAFE LAW — the field watches its own frame rate (sculpted beings, Terror Toonz)
import { FrameWatch } from "./sculptGate";
import { cdnUrl, cardUrl } from "./CDN";

// ── Card lookup (static data — renderer-side lookup is fine) ──
const ALL_CARDS_FLAT = [
  ...ESCENDANTS, ...TOME_CARDS, ...IMPERA, ...UHLTIS,
  ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS,
];
// ── BASE-AWARE (July 28, 2026) — THE PHANTOM ESCLING FIX ──────────
// The Necro Seal stamps a ruined being: spectral_tiger_lord becomes
// spectral_tiger_lord~0~3, and it keeps that tag when it comes back to
// the field. Shatterfield.js's getCard learned to strip the tag when the
// seal shipped. THIS one — the renderer's own private copy — did not.
//
// Every downstream failure caP'n saw traces to this single line:
//   • FaceSurface bailed (`if (!card) return undefined`), so the CSS3D
//     card face never mounted — a blank slab over the Escling, which is
//     exactly what "the Escling replaced the picture" looks like.
//   • StatOverlay returned null — no Lyt/Lyf gems.
//   • scoreFor(getCard(ce), …) got null, so the Rite of Return and every
//     Lyt attack score silently found nothing. That was the "no effect."
//
// baseId() is the same strip Shatterfield, NPC and the gate all use, so
// there is now one definition of "which card is this" across the engine.
const getCard = (id) => (id ? ALL_CARDS_FLAT.find((c) => c.id === baseId(id)) || null : null);

// SHIP 8 — the kit announces its version once, so a stale re-vendor is
// visible in the console instead of in a being's face.
// (A module body runs once per load, so this is once per boot by itself.)
if (typeof console !== "undefined") {
  console.info(`[Shatterfield3D] being kit ${FIELD_BEING_KIT_VERSION}`);
  // The seat layout and the rules must agree on the width of the battle
  // line, or escWorld(i) walks off the end of SEAT.esclings the first time
  // the Mind rift animates. Loud at boot, not silent at the first strike.
  console.assert(SEAT.esclings.length === ESCLING_SLOTS,
    `[Shatterfield3D] SEAT.esclings has ${SEAT.esclings.length} rifts, rules.js says ${ESCLING_SLOTS}`);
}

// ── riftArray — Firebase sparse-array normalizer (defensive copy;
// the subscription chokepoint already normalizes, this is seatbelt) ──
const riftArray = (v, len) => {
  const out = new Array(len).fill(null);
  if (Array.isArray(v)) {
    for (let i = 0; i < len; i++) out[i] = v[i] ?? null;
  } else if (v && typeof v === "object") {
    Object.keys(v).forEach((k) => {
      const n = parseInt(k, 10);
      if (!Number.isNaN(n) && n >= 0 && n < len) out[n] = v[k];
    });
  }
  return out;
};

// ── H21 — THE FLIGHT STAMP ─────────────────────────────────────────
// Every flight leaves this function wearing its type's profile: the
// path style, the arc, the speed, the spin, the tilt, plus the ring
// and sigil it will stamp when it lands. ONE call per flight kind —
// the registry decides everything else, so a new card type costs zero
// lines in the detector below.
//   intent: "play" (default) · "draw" · "depart"
const stampFlight = (flight, card, intent) => {
  const pr = arrivalFor(card, intent);
  const f = pr.flight;
  return {
    ...flight,
    style: f.style, arc: f.arc, speed: f.speed, spin: f.spin, tilt: f.tilt,
    ringProfile: pr.ring, sigil: pr.sigil, sigilColor: pr.color,
    profileName: pr.name,
  };
};

const tomeInfo = (t) => {
  const id = typeof t === "object" && t !== null ? t?.id : t;
  const faceDown = typeof t === "object" && t !== null ? !!t?.faceDown : false;
  return { id: id || null, faceDown };
};

const pileCount = (pile) => {
  if (!pile) return 0;
  if (Array.isArray(pile)) return pile.length;
  if (typeof pile === "object") return Object.values(pile).reduce((a, b) => a + (+b || 0), 0);
  return 0;
};

// Minimal event shim — parent handlers only read clientX / clientY
// (radial positioning via smartPos). R3F events carry them; fall back
// to screen center so a radial can never spawn off-world.
const evShim = (e) => ({
  clientX: e?.clientX ?? (typeof window !== "undefined" ? window.innerWidth / 2 : 0),
  clientY: e?.clientY ?? (typeof window !== "undefined" ? window.innerHeight / 2 : 0),
  stopPropagation: () => {},
});

// ── mulberry32 — THE seeded PRNG (copy verbatim into every IE 3D game) ──
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const seedOf = (str) => {
  let h = 9;
  const s = String(str || "seed");
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 387420489);
  return h >>> 0;
};
const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);

// ═══ STAT-OVERLAY ZOOM GATE (Ship 4.2) ═══
// caP'n's law: digital values ride the beings at DEFAULT and slightly-in,
// then FADE OUT once you're close enough that only ~4–6 cards fill the
// screen (you can read the printed art at that range — the gems would just
// clutter). One module-level number, written ONCE per frame by CameraRig
// (never setState — perf law), read imperatively by each gem's own useFrame
// which lerps CSS opacity. Zero React re-renders, one source of truth.
//   camDist  = live camera distance from field center.
//   FADE_FAR / FADE_NEAR = the crossover band (world units).
// Above FAR → fully shown. Below NEAR → fully hidden. Between → linear.
const CAM_STATE = { dist: 14 };

// ═══ SHIP 6 — THE FX BUS ═══
// Same law as CAM_STATE: React context does NOT cross the <Canvas>
// boundary, and setState in useFrame is banned (ie-realtime-perf). So
// the transition detector (outside the Canvas) WRITES this module-level
// object and the in-world effects READ it every frame. One writer, many
// readers, zero re-renders.
//   shakeUntil/shakeSpan/shakeMag — the world-shake on an Anima strike.
//   sweepAt                       — timestamp of the last turn change.
const FX_BUS = { shakeUntil: 0, shakeSpan: 1, shakeMag: 0, sweepAt: 0 };
const FADE_FAR = 8.0;   // ≳ table & every preset (theirs≈8.65) → values shown
const FADE_NEAR = 5.6;  // hand-orbit past this → only a few cards → hidden
const statVis = (d) =>
  d >= FADE_FAR ? 1 : d <= FADE_NEAR ? 0 : (d - FADE_NEAR) / (FADE_FAR - FADE_NEAR);

// ═══════════════════════════════════════════════════════════════════
// PROCEDURAL TEXTURES — canvas → CanvasTexture, cached by key.
// Zero image assets in Ship 1. (Ship 4 swaps faces for CDN art.)
// ═══════════════════════════════════════════════════════════════════
const TEX_CACHE = new Map();
const sRGB = (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; return t; };

function wrapName(g, text, maxW) {
  const words = String(text || "?").split(" ");
  const lines = []; let line = "";
  words.forEach((w) => {
    const test = line ? line + " " + w : w;
    if (g.measureText(test).width > maxW && line) { lines.push(line); line = w; }
    else line = test;
  });
  if (line) lines.push(line);
  return lines.slice(0, 2);
}

// Card face — SHIP 4 frame: name banner, ART WINDOW (real CDN art
// overlays it via <ArtPlane>), type + level, school · ilk, stat footer.
// The procedural window doubles as the loading/fallback state.
const ART_RECT = { x: 22, y: 76, w: 212, h: 156 }; // on the 256×358 canvas
function faceTexture(card) {
  const key = `face4|${card?.id || "unknown"}`;
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = 256; c.height = 358;
  const g = c.getContext("2d");

  g.fillStyle = "#100c20"; g.fillRect(0, 0, 256, 358);
  const grad = g.createLinearGradient(0, 0, 0, 358);
  grad.addColorStop(0, "#1a1435"); grad.addColorStop(1, "#0a0718");
  g.fillStyle = grad; g.fillRect(8, 8, 240, 342);
  g.strokeStyle = SD.gold; g.lineWidth = 5; g.strokeRect(8, 8, 240, 342);
  g.strokeStyle = `${SD.gold}55`; g.lineWidth = 1.5; g.strokeRect(16, 16, 224, 326);

  // Name banner (up to two lines)
  g.fillStyle = "#f0d060";
  g.font = "bold 24px 'Cinzel', 'Palatino Linotype', serif";
  g.textAlign = "center";
  const lines = wrapName(g, card?.name || card?.id || "?", 208);
  if (lines.length === 1) g.fillText(lines[0], 128, 52);
  else { g.fillText(lines[0], 128, 42); g.fillText(lines[1], 128, 66); }

  // Art window — placeholder sigil until (or unless) CDN art lands
  const A = ART_RECT;
  g.fillStyle = "#0b0820"; g.fillRect(A.x, A.y, A.w, A.h);
  g.strokeStyle = `${SD.gold}44`; g.lineWidth = 1.5; g.strokeRect(A.x, A.y, A.w, A.h);
  const cx = A.x + A.w / 2, cy = A.y + A.h / 2;
  g.strokeStyle = `${SD.gold}33`; g.lineWidth = 1;
  g.beginPath(); g.arc(cx, cy, 30, 0, Math.PI * 2); g.stroke();
  g.fillStyle = `${SD.gold}2e`;
  g.beginPath();
  g.moveTo(cx, cy - 22); g.lineTo(cx + 17, cy); g.lineTo(cx, cy + 22); g.lineTo(cx - 17, cy);
  g.closePath(); g.fill();

  // Type + level pips
  g.fillStyle = "#b3a68c";
  g.font = "italic 17px 'Palatino Linotype', serif";
  const pips = card?.level ? " " + "◆".repeat(Math.max(1, Math.min(4, card.level))) : "";
  g.fillText(`${card?.type || ""}${pips}`, 128, 258);

  // Ilk · School — top-left stamp first, top-right second, the same
  // order the card face prints them. Until the 2026-08-25 field-name
  // fix this read [school, ilk] to get that order out of inverted data;
  // the printed line is byte-identical, the fields just say what they
  // hold now.
  if (card?.ilk || card?.school) {
    g.fillStyle = "#7d7191";
    g.font = "13px 'Palatino Linotype', serif";
    g.fillText([card?.ilk, card?.school].filter(Boolean).join(" · "), 128, 280);
  }

  // NOTE (Ship 4.1): the fallback slab no longer draws stat words/digits.
  // The DIGITAL STAT OVERLAY owns every stat on the field now (Ship 4.2) —
  // live LYF/LYT ride the card face as billboarded gems, art or no art.

  const t = sRGB(new THREE.CanvasTexture(c));
  TEX_CACHE.set(key, t);
  return t;
}

// ═══ SHIP 4 — REAL CARD ART FROM THE CDN ═══
// One URL builder, never scattered concatenation (ie-cdn-assets law).
// July 28, 2026 — this file used to carry its OWN hardcoded copy of the
// domain, which is precisely the drift that law exists to stop: when
// cardUrl learned to strip the Necro Seal, this line did not, and the 3D
// board kept asking for `<id>~0~3.webp`. There is now exactly ONE place
// in the engine that knows how to build a card's art URL.
const cardArtUrl = (cardId) => cardUrl(cardId);

// Art cache: id → { status: "loading"|"ok"|"fail", tex?, promise }
// KEYED BY BASE ID. Three sealed copies of the same Beb are three
// different strings but one picture — keying by the raw id would load
// and hold the identical webp three times, and a Beb that has come back
// twice would re-download its own face on every return.
const ART_CACHE = new Map();
function loadCardArt(cardId) {
  const key = baseId(cardId);
  const hit = ART_CACHE.get(key);
  if (hit) return hit.promise;
  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");
  const promise = new Promise((resolve) => {
    loader.load(
      cardArtUrl(key),
      (t) => {
        t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
        ART_CACHE.set(key, { status: "ok", tex: t, promise });
        resolve(t);
      },
      undefined,
      () => { ART_CACHE.set(key, { status: "fail", promise }); resolve(null); }
    );
  });
  ART_CACHE.set(key, { status: "loading", promise });
  return promise;
}

function useCardArt(cardId, enabled) {
  const [tex, setTex] = useState(() => {
    const e = cardId ? ART_CACHE.get(baseId(cardId)) : null;
    return e?.status === "ok" ? e.tex : null;
  });
  useEffect(() => {
    if (!enabled || !cardId) return undefined;
    let alive = true;
    loadCardArt(cardId).then((t) => { if (alive && t) setTex(t); });
    return () => { alive = false; };
  }, [cardId, enabled]);
  return enabled ? tex : null;
}

// The art IS the card — it fills the whole face, edge to edge (the CDN
// webp already carries frame, name, and printed stats). A child plane of
// the card mesh, so it inherits shake / breathe / flight flips for free.
function ArtPlane({ cardId, t = CARD.T }) {
  const tex = useCardArt(cardId, !!cardId);
  if (!tex) return null;
  return (
    <mesh position={[0, t / 2 + 0.0015, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[CARD.W * 0.985, CARD.H * 0.985]} />
      <meshStandardMaterial map={tex} emissiveMap={tex} emissive={"#ffffff"}
        emissiveIntensity={0.2} roughness={0.6} />
    </mesh>
  );
}

// ═══ THE ONE FACE — <CardFace> MOUNTED IN THE WORLD (CSS3D) ═══════
// The card face is the shop's <CardFace>, rendered as real DOM in the
// card's plane by three's CSS3DRenderer — NOT by drei's <Html transform>,
// which mounts nothing on caP'n's machine (July 24 + 25, twice proven;
// plain <Html> billboards like the stat gems paint fine, so the failure
// is transform mode itself). DELETE THE DEGREE OF FREEDOM, NOT THE BUG:
// transform mode is gone from this file entirely.
//
// How it works:
//   • Shatterfield3D owns an overlay <div> over the Canvas and one
//     CSS3DRenderer + CSS3D scene (the CSS_API store below).
//   • <CssSync> inside the Canvas re-renders the CSS3D scene with the
//     SAME camera every frame — DOM faces track orbits, presets, and
//     flights in perfect lockstep with the WebGL table.
//   • Each face is a CSS3DObject whose matrix is copied from its card
//     mesh's matrixWorld every frame, times a local lift+lay+scale, so
//     a face inherits every shake, breathe, flip, and flight for free.
//   • React content goes in via createPortal — the CardFace is a live
//     component (art loads, foil animates), not a snapshot.
// If the CSS3D api isn't up (first paint, SSR), Card3D falls back to
// the old ArtPlane + native FoilPlane — never a blank board.
const FACE_PX_W = 200;
const FACE_PX_H = Math.round(FACE_PX_W * (CARD.H / CARD.W)); // 280

// ⚠ NOT React context — context does NOT cross the R3F <Canvas> boundary
// (the canvas runs its own reconciler), so a Provider outside would read
// null forever inside. Module-level store + tiny subscription instead,
// the same pattern CAM_STATE already uses. One board mounts at a time
// (the 2D↔3D toggle unmounts the other), so a singleton is correct.
const CSS_API = { current: null, listeners: new Set() };
function setCssApi(v) { CSS_API.current = v; CSS_API.listeners.forEach((f) => f()); }
function useCssApi() {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    CSS_API.listeners.add(f);
    return () => CSS_API.listeners.delete(f);
  }, []);
  return CSS_API.current;
}

// Runs INSIDE the Canvas: sizes the CSS3D renderer to the viewport and
// renders the CSS3D scene with the live camera, every frame, after the
// scene graph has updated. Priority stays 0 — a positive priority would
// switch R3F to manual-render mode and black out the WebGL board.
function CssSync() {
  const api = useCssApi();
  const { camera, size } = useThree();
  useFrame(() => {
    if (!api) return;
    if (api.w !== size.width || api.h !== size.height) {
      api.renderer.setSize(size.width, size.height);
      api.w = size.width; api.h = size.height;
    }
    api.renderer.render(api.scene, camera);
  });
  return null;
}

// ── THE FACE REGISTRY ─────────────────────────────────────────────
// ⚠ THE JULY 25 LESSON: react-dom's createPortal CANNOT be returned
// from a component inside <Canvas> — the R3F reconciler owns that tree
// and tries to build <div>/<img> as THREE objects ("Img is not part of
// the THREE namespace"). Layer-7 law, literally: DOM is rendered by the
// DOM tree. So the in-canvas side only REGISTERS a face (CSS3DObject +
// per-frame matrix/opacity, all imperative), and <FaceLayer> — plain
// React OUTSIDE the Canvas — portals the live <CardFace> into each
// registered element.
const FACES = { map: new Map(), listeners: new Set() };
function facesNotify() { FACES.listeners.forEach((f) => f()); }
function useFaceEntries() {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    FACES.listeners.add(f);
    return () => FACES.listeners.delete(f);
  }, []);
  return Array.from(FACES.map.values());
}

// Renders OUTSIDE the Canvas (plain DOM tree). One portal per face.
function FaceLayer() {
  const entries = useFaceEntries();
  return (
    <>
      {entries.map((e) => domPortal(
        <div style={{ width: FACE_PX_W, height: FACE_PX_H, pointerEvents: "none" }}>
          {/* The shop's card. Unmodified. That is the entire point.
              H22c — `stamps` reads through the registry entry's REF, so a
              fresh closure from the parent every frame costs nothing; only
              a real change (word or state) re-notifies this layer. */}
          <CardFace
            card={e.card} cardId={e.cardId} holoTier={e.tier}
            width={FACE_PX_W} height={FACE_PX_H}
            k={FACE_PX_W / 88} size="normal" radius={9}
            stamps={e.stampsRef ? e.stampsRef.current : null}
          />
        </div>,
        e.el, e.key
      ))}
    </>
  );
}

// Lives INSIDE the Canvas. Renders nothing — owns the CSS3DObject, its
// matrix sync, its gameplay dim, and its registry entry.
let FACE_SEQ = 0;
function FaceSurface({ cardId, tier = null, thick = CARD.T, dimRef = null, meshRef, stamps = null, alphaRef = null }) {
  const api = useCssApi();
  const card = getCard(cardId);
  const objRef = useRef(null);
  const cur = useRef(1);
  const key = useRef(`face-${++FACE_SEQ}`).current;

  // ── H22c — PRIMERA STAMPS THROUGH THE REGISTRY ──────────────────
  // `stampsFor()` hands back a fresh object with fresh closures on every
  // parent render. Putting that object in the registry entry's dep list
  // would delete + re-add the entry constantly, remounting <CardFace>
  // (killing the punch animation mid-press and re-fetching the art).
  // So: the LIVE object rides a ref that's refreshed every render, and a
  // cheap SIGNATURE — the only thing that changes pixels — decides when
  // the DOM layer needs to hear about it.
  const stampsRef = useRef(stamps);
  stampsRef.current = stamps;
  const stampSig = stamps
    ? `${stamps.ilk?.word || ""}:${stamps.ilk?.state || ""}|${stamps.school?.word || ""}:${stamps.school?.state || ""}`
    : "";

  // The portal target — one plain div per face, owned for the face's life.
  const [el] = useState(() => {
    const d = document.createElement("div");
    d.style.pointerEvents = "none";
    d.style.willChange = "transform, opacity";
    return d;
  });

  // Local transform: lift to the slab's top, lay flat (+y up), scale
  // the FACE_PX_W-px div onto the card's world footprint.
  const local = useMemo(() => {
    const m = new THREE.Matrix4();
    m.multiply(new THREE.Matrix4().makeTranslation(0, thick / 2 + 0.004, 0));
    m.multiply(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
    m.multiply(new THREE.Matrix4().makeScale(CARD.W / FACE_PX_W, CARD.H / FACE_PX_H, 1));
    return m;
  }, [thick]);

  // CSS3DObject lifecycle — into the scene when the api is up.
  //
  // ⚠ THE CLICK-EATER (July 25, second CSS3D trap). `new CSS3DObject(el)`
  // WRITES `element.style.pointerEvents = "auto"` in its own constructor —
  // three's source does this so CSS3D UIs can be clicked. That overrode the
  // `none` set above, and `pointer-events:auto` on a descendant punches
  // straight through the host div's `pointer-events:none`. Result: every
  // FACE-UP card wore an invisible 200×280 DOM rectangle over its mesh, so
  // WebGL picking never saw it — no click, no hover, no peek. The only cards
  // that still worked were the ones with NO DOM face: the face-down draw
  // piles and the opponent's hand backs. Exactly the symptom caP'n reported.
  // So: re-disable it AFTER construction, never before. The face is a
  // decal, not a button — the mesh underneath is the only thing clickable.
  useEffect(() => {
    if (!api) return undefined;
    const obj = new CSS3DObject(el);
    el.style.pointerEvents = "none"; // ← must come AFTER the constructor
    obj.matrixAutoUpdate = false;
    api.scene.add(obj);
    objRef.current = obj;
    return () => { api.scene.remove(obj); objRef.current = null; };
  }, [api, el]);

  // Registry entry — what <FaceLayer> renders into `el`.
  useEffect(() => {
    if (!card) return undefined;
    FACES.map.set(key, { key, el, card, cardId, tier, stampsRef });
    facesNotify();
    return () => { FACES.map.delete(key); facesNotify(); };
  }, [key, el, card, cardId, tier, stampsRef]);

  // Seal state changed (ready → spent, locked → ready, word swapped by an
  // Ascend): repaint the DOM layer WITHOUT touching the entry's identity.
  useEffect(() => {
    if (FACES.map.has(key)) facesNotify();
  }, [key, stampSig]);

  useFrame(() => {
    const obj = objRef.current;
    const mesh = meshRef && meshRef.current;
    if (!obj || !mesh) return;
    mesh.updateWorldMatrix(true, false);
    obj.matrix.copy(mesh.matrixWorld).multiply(local);
    // GAMEPLAY BEATS COSMETIC — face (art + foil together) steps back
    // while a game light is on the card. Imperative, never state.
    const target = dimRef && dimRef.current ? 0.55 : 1;
    cur.current += (target - cur.current) * 0.15;
    // H21 — THE FADE. A Phantom's strike thins the FACE while the mesh
    // underneath stays solid and clickable. The dim above lerps (it is
    // a state change); the strike alpha does NOT — it is already a
    // curve, and lerping a curve smears it into mush.
    const a = alphaRef ? alphaRef.current : 1;
    el.style.opacity = (cur.current * a).toFixed(3);
  });

  return null; // nothing in the R3F tree — the DOM side owns the pixels
}

// ═══ DIGITAL STAT OVERLAY (Ship 4.2 — ALWAYS UP) ═══
// caP'n's 4.2 ruling: the values stay UP. Printed art is too small to read
// at table distance, so every being on the field wears its LIVE stats as
// billboarded gems — same glyphs, same left/right order, same color law as
// the 2D field card (StatGlyph.js is the one source of the shapes).
//
// ⚠ ANCHOR LAW (the 4.2 bug): on a Terror card the footer reads
//        LYF (shield) LOWER-LEFT  ·  LYT (blade) LOWER-RIGHT
// exactly as `<PlayerSide>` lays it out in Shatterfield.js. These were
// transposed in 4.1, so damage numbers landed on the blade's corner.
// Card-local axes: −x = the card's left, +z = the card's bottom edge.
const STAT_ANCHOR = {
  lyf: [-CARD.W * 0.34, CARD.H * 0.37],   // lower-LEFT  [x, z] on the face
  lyt: [CARD.W * 0.34, CARD.H * 0.37],    // lower-RIGHT
};

// Color law, lifted verbatim from the 2D card footer so a stat never
// means two different things on two different surfaces:
//   LYF — blue at print · red when fractured (below print) · gold when buffed
//   LYT — red  at print · gold when buffed · ember when suppressed
const STAT_TONE = {
  lyf: (v, base) => (base != null && v < base ? "#e05555"
    : base != null && v > base ? "#f0d060" : "#4a9fd4"),
  lyt: (v, base) => (base != null && v > base ? "#f0d060"
    : base != null && v < base ? "#ff9a6b" : "#e05555"),
};

// A plain centered <Html> — a SCREEN-SPACE billboard. This is the config
// that actually renders (the 4.1 `transform sprite` combo silently drew
// nothing on a flat-rotated node). It always faces the camera, needs no
// rotation math, and reads right-side-up over the opponent's π-rotated
// seat for free. The zoom gate lerps its opacity every frame.
function DigitalStat({ value, side, tone }) {
  const wrapRef = useRef();
  const cur = useRef(1);
  useFrame(() => {
    const el = wrapRef.current;
    if (!el) return;
    const target = statVis(CAM_STATE.dist);
    cur.current += (target - cur.current) * 0.2;
    const o = cur.current;
    el.style.opacity = o.toFixed(3);
    // Fully faded → also drop it from hit/paint entirely.
    el.style.visibility = o < 0.02 ? "hidden" : "visible";
  });
  return (
    <Html
      position={[STAT_ANCHOR[side][0], CARD.T / 2 + 0.02, STAT_ANCHOR[side][1]]}
      center occlude={false} zIndexRange={[18, 10]}
      style={{ pointerEvents: "none" }}
    >
      <div ref={wrapRef} style={{
        display: "inline-flex", alignItems: "center", gap: 3, lineHeight: 1,
        background: "rgba(0,0,6,0.66)", borderRadius: 7,
        padding: "2px 7px 2px 6px", border: `1px solid ${tone}55`,
        boxShadow: `0 0 8px ${tone}33, 0 2px 6px rgba(0,0,0,0.9)`,
        whiteSpace: "nowrap", willChange: "opacity",
      }}>
        <StatGlyph kind={side} size={16} color={tone} />
        <span style={{
          fontFamily: GM.font, fontWeight: 900, fontSize: 20, color: tone,
          textShadow: `0 0 5px ${tone}66, 0 1px 3px #000`,
        }}>{value}</span>
      </div>
    </Html>
  );
}

// Both gems, always, for any being holding values. The 4.1 `artless` gate
// is gone entirely — an unminted card simply shows the same gems it would
// have shown anyway, which is the whole point of a live overlay.
function StatOverlay({ cardId, lytShow, lyfShow }) {
  const card = getCard(cardId);
  if (!card) return null;
  const baseLyt = card.lyt ?? null;
  const baseLyf = card.lyf ?? null;
  if (lytShow == null && lyfShow == null) return null;
  return (
    <group>
      {lyfShow != null && (
        <DigitalStat value={lyfShow} side="lyf" tone={STAT_TONE.lyf(lyfShow, baseLyf)} />
      )}
      {lytShow != null && (
        <DigitalStat value={lytShow} side="lyt" tone={STAT_TONE.lyt(lytShow, baseLyt)} />
      )}
    </group>
  );
}

// ⭐ THE REAL CARD BACK (caP'n, 2026-08-22): *"the Shatterfield
// Luxmoire and Grimoire — you created a back of the card. Please just
// use the back of my card for all of it."*
//
// He drew one. It has been sitting on the CDN as `card_back.webp` the
// whole time, and the 2D field has been using it since it shipped —
// this file was the only surface still drawing an invented one, a
// canvas obsidian slab with a gold ring and a four-point diamond that
// nobody asked for and that agreed with nothing else in the game.
//
// ── HOW THE SWAP IS SAFE ────────────────────────────────────────
// A TextureLoader is asynchronous and a material is built the instant
// the first face-down card mounts, so a naive `load()` gives one frame
// of an untextured white plane. So the PROCEDURAL BACK STAYS, demoted
// from "the back" to "the thing on screen for the 40ms before the real
// one arrives" — the texture is created from the canvas immediately,
// and the loader overwrites its image and flags `needsUpdate` when the
// WebP lands. One texture object, one material, no re-render, and no
// white flash.
//
// If the WebP 404s the canvas back simply stays, which is the same
// graceful failure the 2D field's `onError` gives it.
//
// ⚠ THIS ONE **NEEDS** `crossOrigin`, AND THE FIRST DRAFT SAID THE
// OPPOSITE. The holofoil law — "NO crossOrigin on card-art <img>s" —
// is about the DOM, where a tagged load can hard-fail and a plain one
// merely taints a canvas nobody reads. WebGL is the reverse: uploading
// a NON-CORS image through texImage2D is a SecurityError, not a silent
// taint, and the throw lands inside the render loop. This file already
// knew that — `loadCardArt` calls `setCrossOrigin("anonymous")` for
// every card FACE off the same host and has shipped for months, which
// also proves terror-assets does serve the header. Caught by an
// adversarial pass; the wrong version of this comment would have been
// copied into the next surface.
function backTexture() {
  const key = "back";
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);

  // The placeholder, drawn once, shown for as long as the network takes.
  const c = document.createElement("canvas");
  c.width = 256; c.height = 358;
  const g = c.getContext("2d");
  g.fillStyle = "#0a0718"; g.fillRect(0, 0, 256, 358);
  g.strokeStyle = `${SD.gold}88`; g.lineWidth = 4; g.strokeRect(10, 10, 236, 338);
  g.strokeStyle = `${SD.gold}33`; g.lineWidth = 1; g.strokeRect(20, 20, 216, 318);
  g.strokeStyle = SD.gold; g.lineWidth = 2;
  g.beginPath(); g.arc(128, 179, 54, 0, Math.PI * 2); g.stroke();
  g.fillStyle = SD.gold;
  g.beginPath();
  g.moveTo(128, 137); g.lineTo(160, 179); g.lineTo(128, 221); g.lineTo(96, 179);
  g.closePath(); g.fill();
  g.fillStyle = "#fff3c4";
  g.beginPath(); g.arc(128, 179, 7, 0, Math.PI * 2); g.fill();

  const t = sRGB(new THREE.CanvasTexture(c));
  TEX_CACHE.set(key, t);

  // ⭐ And then the real one, over the top of it.
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";   // ⚠ REQUIRED — see the banner above
    img.onload = () => {
      t.image = img;
      t.needsUpdate = true;
    };
    img.src = cdnUrl("card_back.webp");
  } catch (_) { /* the canvas back is already in the cache; nothing to undo */ }

  return t;
}

// SHIP 5 — every card in the game has the SAME back, and nothing ever
// animates it. One material, made on first use, shared by the field.
let BACK_MAT = null;
function sharedBackMat() {
  if (!BACK_MAT) {
    BACK_MAT = new THREE.MeshStandardMaterial({ map: backTexture(), roughness: 0.7 });
  }
  return BACK_MAT;
}

// Rift pad — dark slab with a dashed gold border + faint zone label.
function riftTexture(label) {
  const key = `rift|${label}`;
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = 128; c.height = 179;
  const g = c.getContext("2d");
  g.fillStyle = "rgba(13,10,40,0.85)"; g.fillRect(0, 0, 128, 179);
  g.strokeStyle = `${SD.gold}66`; g.lineWidth = 2.5;
  g.setLineDash([9, 7]); g.strokeRect(6, 6, 116, 167);
  g.setLineDash([]);
  g.fillStyle = `${SD.gold}55`;
  g.font = "bold 15px 'Palatino Linotype', serif";
  g.textAlign = "center";
  g.fillText(label, 64, 96);
  const t = sRGB(new THREE.CanvasTexture(c));
  TEX_CACHE.set(key, t);
  return t;
}

// Table felt — deep obsidian noise + gold frame + broken seam hairline.
function feltTexture() {
  const key = "felt";
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = 1024; c.height = 668;
  const g = c.getContext("2d");
  g.fillStyle = "#07051a"; g.fillRect(0, 0, 1024, 668);
  const R = mulberry32(777);
  for (let i = 0; i < 2600; i++) {
    const x = R() * 1024, y = R() * 668, a = R() * 0.05;
    g.fillStyle = `rgba(200,180,255,${a})`;
    g.fillRect(x, y, 1.6, 1.6);
  }
  // Gold frame
  g.strokeStyle = `${SD.gold}77`; g.lineWidth = 5; g.strokeRect(14, 14, 996, 640);
  g.strokeStyle = `${SD.gold}2a`; g.lineWidth = 2; g.strokeRect(28, 28, 968, 612);
  // The Shatter Seam — broken at center so the sigil owns the middle.
  const mid = 334;
  const seam = g.createLinearGradient(30, 0, 994, 0);
  seam.addColorStop(0, "rgba(200,150,60,0)");
  seam.addColorStop(0.15, `${SD.gold}66`);
  seam.addColorStop(0.44, `${SD.gold}33`);
  seam.addColorStop(0.47, "rgba(200,150,60,0)");
  seam.addColorStop(0.53, "rgba(200,150,60,0)");
  seam.addColorStop(0.56, `${SD.gold}33`);
  seam.addColorStop(0.85, `${SD.gold}66`);
  seam.addColorStop(1, "rgba(200,150,60,0)");
  g.strokeStyle = seam; g.lineWidth = 2;
  g.beginPath(); g.moveTo(30, mid); g.lineTo(994, mid); g.stroke();
  const t = sRGB(new THREE.CanvasTexture(c));
  TEX_CACHE.set(key, t);
  return t;
}

// ── Cursor helper — event-driven, never per-frame ──
const cursor = {
  over: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
  out: () => { document.body.style.cursor = "default"; },
};

// ── Hover-preview reporting ──────────────────────────────────────────
// Every card mesh (Card3D, and HandCard's own hover pad) reports what's
// under the cursor through this context. The actual centered "peek"
// overlay is DOM chrome and lives in Shatterfield.js (Layer 7 law: this
// file stays a pure renderer — it reports, it never draws its own modal
// layer). `token` lets a fast pointerOut→pointerOver swap between two
// overlapping cards resolve to the right one instead of flapping to
// null; faceDown cards report cardId:null so a hidden card can never
// leak its identity through the preview, only through the material.
const HoverReportCtx = createContext(null);

// ═══════════════════════════════════════════════════════════════════
// CARD MESH — one thin box per card. Face on +Y (cards lie on felt).
// glow: steady gold. pulse: color that breathes (targeting / attacker).
// breathe: idle float for beings (nothing static ever looks alive).
// ═══════════════════════════════════════════════════════════════════
function Card3D({ cardId, faceDown = false, position, rotation = [0, 0, 0],
                  glow = false, pulse = null, breathe = false, onClick,
                  shakeUntil = 0, overlay = null, tier = null, lunge = false,
                  stamps = null, strike = null, impact = null,
                  // SHIP 8 — a rift with a standing body. `rider` is mounted
                  // INSIDE this mesh (on its top face) so the body inherits
                  // every lunge, strike, shake and breathe the card already
                  // performs — ONE WRITER, still. `noDomFace` takes the CSS3D
                  // decal off this card: DOM paints over WebGL regardless of
                  // depth, so a face left on would sit in front of the body
                  // standing on it. The card falls back to the ArtPlane +
                  // native foil path that already exists for the CSS3D-down
                  // case, and its two Primera seals come back as billboards
                  // (<SealBillboards>) — same component, same press path.
                  rider = null, noDomFace = false }) {
  const ref = useRef();
  // SHIP 6 — THE LUNGE. A declared attacker doesn't just glow: it rises
  // off the felt and leans across the seam at the seat it's about to
  // hit. Seat-local −z is "toward the enemy" for BOTH chairs (the
  // opponent's group is π about Y), so one number serves both. Eased on
  // a ref, never state — the card is mid-combat, not mid-render.
  const lungeK = useRef(0);
  // ⬡ H21 — THE STRIKE. The lunge above is the SUSTAINED lean of a
  // declared attacker choosing its target. This is the swing itself —
  // the tick `attackedThisTurn` flips false→true — and until today the
  // 3D board had no motion for it at all. Space fires The Vector,
  // Phantom The Fade, Dragon The Maul, Time The Echo; anything else
  // keeps The Lunge as the honest floor. One timeline on a ref, sampled
  // per frame, composed into the ONE WRITER below as another TERM.
  const strikeT = useRef({ at: 0, t: 1 });
  // The Fade is the one profile that touches opacity. The mesh stays
  // solid (a ghost you can't click is a bug); it is the CSS3D FACE that
  // thins, through the same imperative channel the gameplay dim uses.
  const strikeAlpha = useRef(1);
  const card = getCard(cardId);
  const seed = useMemo(() => seedOf(cardId), [cardId]);

  // ⬡ ONE FACE — is the CSS3D layer up? (It always is once the overlay
  // div has mounted; null only on the very first paint.) When absent,
  // the old ArtPlane + native FoilPlane path keeps the board alive.
  const domFaceOk = !!useCssApi() && !noDomFace;

  // ═══ SHIP 5 — THE SLAB ═══
  // A face-down card is PLAIN, always. Thickness and a lit rim would
  // leak "this copy is foil" across the seam; the 2D board shows a
  // hidden card nothing, and neither does this one.
  const foil = !faceDown && hasFoil3D(tier) ? tier : null;
  const thick = holoThickness(foil);
  // BOTTOM-FACE LAW: callers place a card by where a BASE-thickness
  // card's center would sit. A thick card keeps its underside on that
  // same plane and grows upward, so nothing beneath it ever moves.
  const baseY = position[1] + (thick - CARD.T) / 2;
  // Live 1/0 flag: is a GAMEPLAY light on this card right now? The foil
  // reads it and steps aside. Written in useFrame, never state.
  const gameLit = useRef(0);
  const reportHover = useContext(HoverReportCtx);
  const hoverToken = useRef(Math.random().toString(36).slice(2)).current;
  const handlePointerOver = (e) => {
    e.stopPropagation();
    if (onClick) document.body.style.cursor = "pointer";
    if (reportHover) reportHover({ token: hoverToken, cardId: faceDown ? null : cardId, faceDown });
  };
  const handlePointerOut = () => {
    if (onClick) document.body.style.cursor = "default";
    if (reportHover) reportHover({ token: hoverToken, clear: true });
  };
  // A card that leaves the scene (played, ascended, transcended, sent to
  // the necropolis) while still hovered never fires pointerOut — clear
  // this instance's claim on the preview so it doesn't stick open.
  useEffect(() => () => { if (reportHover) reportHover({ token: hoverToken, clear: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mats = useMemo(() => {
    const face = faceDown ? backTexture() : (card ? faceTexture(card) : backTexture());
    // SHIP 5 — a plain edge is dead matte board that never animates, so
    // every plain card in the duel shares ONE material. A foil edge IS
    // animated, so it must be per-instance or the whole field would
    // pulse in lockstep.
    const edge = foil ? holoEdgeMaterial(foil) : PLAIN_EDGE;
    const top = new THREE.MeshStandardMaterial({
      map: face, emissiveMap: face, emissive: new THREE.Color("#000000"),
      emissiveIntensity: 0, roughness: 0.55, metalness: 0.1,
    });
    // The back is identical on every card in the game — share it too.
    const bottom = sharedBackMat();
    // box material order: +x, −x, +y (top/face), −y (bottom/back), +z, −z
    return [edge, edge, top, bottom, edge, edge];
  }, [card, faceDown, foil]);

  useFrame((state, dt) => {
    const topMat = mats[2];
    const tSec = state.clock.elapsedTime;
    const now = Date.now();
    const step = Math.min(dt || 0.016, 0.05); // tab-return guard

    // ── H21 — advance the strike timeline ──────────────────────────
    // A new `strike.at` restarts the swing. The profile's own ms sets
    // the span, and T17's rate multiplies it, so QUICK runs the same
    // curve faster rather than a different curve.
    const sT = strikeT.current;
    // ⚠ THE REMOUNT GUARD. `fx.strikes` is a keyed map that keeps the
    // last entry per slot (bounded — six keys, two seats × three
    // slots), so a card that REMOUNTS mid-turn (an Ascend swaps the
    // cardId, a Transcend replaces the being) would find a live-looking
    // entry sitting there and swing again for no reason. A strike is
    // only ever fresh for its own span, so an entry older than a second
    // is history, not an order.
    if (strike && strike.at && strike.at !== sT.at) {
      sT.at = strike.at;
      sT.t = (now - strike.at) < 1200 ? 0 : 1;
    }
    let sp = null;
    if (strike && sT.t < 1) {
      const span = Math.max(0.08, ((strike.profile?.ms || 380) / 1000) / (strike.rate || 1));
      sT.t = Math.min(1, sT.t + step / span);
      sp = strikePose(strike.profile, sT.t, strike.profile?.power || 1);
    }
    strikeAlpha.current = sp ? sp.alpha : 1;

    // SHIP 3 — pulse may be a plain color (while prop is set) or a
    // timed { color, until } fx entry that expires on its own.
    const pulseColor = typeof pulse === "string" ? pulse
      : (pulse && pulse.color && now < (pulse.until || 0)) ? pulse.color : null;
    if (pulseColor) {
      topMat.emissive.set(pulseColor);
      topMat.emissiveIntensity = 0.3 + Math.sin(tSec * 5 + seed % 7) * 0.2;
    } else if (glow) {
      // `glow` may be `true` (the resting gold every selectable card
      // has always worn) or a COLOUR STRING. The string arrived with
      // the draw-pulse tone ruling (2026-08-18): a bought aura has to
      // survive past turn one, and it has to survive in 3D too, without
      // breathing — `pulse` above already owns breathing. One extra
      // typeof, no new prop, and every existing `glow={bool}` call site
      // is untouched.
      topMat.emissive.set(typeof glow === "string" ? glow : SD.gold);
      topMat.emissiveIntensity = 0.55;
    } else if (topMat.emissiveIntensity !== 0) {
      topMat.emissiveIntensity = 0;
    }
    // H21 — the swing's own light. It BEATS the resting glow while it
    // runs (a swing is louder than a selection) but never fights the
    // targeting pulse for the colour slot when the pulse is brighter.
    if (sp && sp.bright > 0.02) {
      const want = sp.bright * 0.95;
      if (want > topMat.emissiveIntensity) {
        topMat.emissive.set(strike.profile?.color || SD.gold);
        topMat.emissiveIntensity = want;
      }
    }
    // SHIP 5 — GAMEPLAY BEATS COSMETIC. Any game light on this card and
    // the foil steps back so a combat signal is never lost to a shiny.
    gameLit.current = (pulseColor || glow || (sp && sp.bright > 0.2)) ? 1 : 0;
    if (ref.current) {
      // ⚠ ONE WRITER LAW (the Ship 6 near-miss): breathe, shake, lunge
      // and now the H21 STRIKE all move the same three numbers. Three
      // independent `if` branches each writing one axis meant the last
      // one to run won and a shaking card stopped breathing. They
      // COMPOSE into a single position.set — add the next effect as
      // another term, never as another branch. The strike obeys the
      // same law: it contributes fwd/up/lat and nothing else touches
      // the write.
      // SHIP 8.3 — a card with a body on it does not swing; the body
      // does (FieldBeing plays the same profile). The card keeps its
      // glow, its pulse and its wound-shake, so the rift still lights.
      lungeK.current += (((lunge && !rider) ? 1 : 0) - lungeK.current) * 0.12;
      const lk = lungeK.current;
      let px = position[0];
      let pz = position[2] - lk * 0.34;
      let py = baseY + lk * 0.22;
      if (breathe) py += Math.sin(tSec * 1.6 + (seed % 100)) * 0.012;
      // SHIP 3 — hit shake: fast decaying jitter until the fx expires.
      // H21 — the wound's amplitude and rate are the VICTIM's own
      // profile now (a Dragon gets rocked, a Phantom barely registers),
      // falling back to the original numbers when none was handed over.
      if (shakeUntil && now < shakeUntil) {
        const span = (impact?.ms || 550);
        const k = (shakeUntil - now) / span; // 1 → 0 decay
        const mag = impact?.mag ?? 0.05;
        const frq = impact?.freq ?? 66;
        px += Math.sin(tSec * frq + seed) * mag * k;
        pz += Math.cos(tSec * (frq * 0.9) + seed) * mag * 0.7 * k;
      }
      // THE STRIKE — seat-local −z is "toward the enemy" for BOTH
      // chairs, so `fwd` needs no per-seat sign.
      const spm = rider ? null : sp;   // the body owns the swing when riding
      if (spm) { pz -= spm.fwd; py += spm.up; px += spm.lat; }
      ref.current.position.set(px, py, pz);
      if (lk > 0.0015 || spm || ref.current.rotation.x !== rotation[0]) {
        ref.current.rotation.x = rotation[0] - lk * 0.30 + (spm ? spm.pitch : 0);
      }
      // Scale is the strike's alone; nothing else on this mesh writes
      // it, so a plain `set` is safe and a no-op while idle.
      if (spm) ref.current.scale.setScalar(spm.scale);
      else if (ref.current.scale.x !== 1) ref.current.scale.setScalar(1);
    }
  });

  // In "surface" mode the face is 2D DOM, so nothing in the world is
  // driving the rim — this does, off the same recipe. Passing null in
  // the other modes keeps the hook unconditional (rules of hooks) while
  // leaving FoilPlane as the sole writer there.
  // When the DOM face carries the shine, nothing in the world drives the
  // milled rim — this hook does, off the 2D recipe's own pulse numbers.
  // In fallback mode FoilPlane's motion() owns the rim; pass null there
  // (hook stays unconditional — rules of hooks).
  useFoilRim(foil, foil && domFaceOk ? mats[0] : null, seed);

  // Per-instance materials are ours to clean up. The shared ones
  // (PLAIN_EDGE, the back) and every module-cached texture are NOT —
  // disposing those would blank the rest of the field.
  useEffect(() => () => {
    mats[2]?.dispose?.();
    if (foil) mats[0]?.dispose?.();
  }, [mats, foil]);

  return (
    <mesh
      ref={ref}
      position={[position[0], baseY, position[2]]}
      rotation={rotation}
      material={mats}
      castShadow
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(evShim(e)); } : undefined}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <boxGeometry args={[CARD.W, thick, CARD.H]} />
      {/* ⬡ THE ONE FACE (July 24 ruling) — the face-up card IS the 2D
          card: <CardFace> mounted in the card's plane, art + scrim +
          text + the real <HoloFoil> all inside it. The procedural mesh
          face beneath doubles as the loading slab. If the DOM layer
          ever fails its liveness probe on a device, every card falls
          back to the old ArtPlane + native FoilPlane in one flip —
          un-canon beats blank, and the console says why. */}
      {/* H22c — the Primera seals ride the ONE FACE like everything else
          on the card. The ArtPlane fallback below carries none: it is the
          un-canon "CSS3D never came up" path, and a board that can't
          render a face can't render a button on one either. */}
      {!faceDown && cardId ? (
        domFaceOk
          ? <FaceSurface cardId={cardId} tier={foil} thick={thick} dimRef={gameLit} meshRef={ref} stamps={stamps} alphaRef={strikeAlpha} />
          : (
            <>
              <ArtPlane cardId={cardId} t={thick} />
              {foil ? <FoilPlane tier={foil} seed={seed} t={thick} dimRef={gameLit} edge={mats[0]} /> : null}
            </>
          )
      ) : null}
      {/* SHIP 4.1 — optional digital stat overlay (invisible unless needed) */}
      {!faceDown ? overlay : null}
      {/* SHIP 8 — the seals, when the DOM face that carried them is off */}
      {!faceDown && noDomFace && stamps ? <SealBillboards stamps={stamps} thick={thick} /> : null}
      {/* SHIP 8 — the body, standing on this card's top face */}
      {rider ? <group position={[0, thick / 2 + 0.003, 0]}>{rider}</group> : null}
    </mesh>
  );
}

// ═══ SHIP 8 — THE SEALS AS BILLBOARDS ═══
// H22c put the two Primera seals on the ONE FACE, and in 3D the CSS3D
// decal is what made them pressable. A standing body takes that decal off
// its rift, so the seals ride the same DOM billboard channel the stat gems
// use, anchored to the card's own top corners (Ilk top-left, School
// top-right — CardRenderer's law). It is the SAME <PrimeraStamp>, the same
// pre-judged `stamps` object, and the press lands in the same
// handlePressPrimera chokepoint; only the surface it sits on changed.
// Zoom-gated like the gems so a wide table shot is not forty buttons.
const SEAL_ANCHOR = {
  left:  [-CARD.W * 0.34, -CARD.H * 0.36],
  right: [ CARD.W * 0.34, -CARD.H * 0.36],
};
function SealBillboard({ stamp, side, thick }) {
  const wrapRef = useRef();
  const cur = useRef(1);
  useFrame(() => {
    const el = wrapRef.current;
    if (!el) return;
    const target = statVis(CAM_STATE.dist);
    cur.current += (target - cur.current) * 0.2;
    const o = cur.current;
    el.style.opacity = o.toFixed(3);
    el.style.visibility = o < 0.02 ? "hidden" : "visible";
  });
  if (!stamp || !stamp.word) return null;
  const k = 1.5; // 17px × k — the 2D card's own knob, one notch up for the table
  // A faded seal is not a button either. ⚠ Not via pointer-events on this
  // wrapper: PrimeraStamp deliberately sets `pointer-events:auto` on
  // itself (that is how it punches through the CSS3D decal's `none`), and
  // a descendant's explicit `auto` beats any ancestor's `none`. So the
  // gate is a CAPTURE-phase stop here, which runs before the stamp's own
  // onClick and cannot be overridden by it.
  const gate = (e) => { if (cur.current < 0.5) { e.stopPropagation(); e.preventDefault(); } };
  return (
    <Html
      position={[SEAL_ANCHOR[side][0], thick / 2 + 0.02, SEAL_ANCHOR[side][1]]}
      center occlude={false} zIndexRange={[18, 10]}
      style={{ pointerEvents: "none" }}
    >
      <div ref={wrapRef} onClickCapture={gate} onPointerDownCapture={gate} style={{
        position: "relative", width: Math.round(23 * k), height: Math.round(30 * k),
        marginTop: -Math.round(13 * k), willChange: "opacity",
      }}>
        <PrimeraStamp stamp={stamp} side={side} k={k} radius={9} />
      </div>
    </Html>
  );
}
function SealBillboards({ stamps, thick }) {
  return (
    <group>
      <SealBillboard stamp={stamps.ilk} side="left" thick={thick} />
      <SealBillboard stamp={stamps.school} side="right" thick={thick} />
    </group>
  );
}

// ── Empty rift pad ──
function Rift({ position, label, onClick, highlight = false }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.material.opacity = highlight
      ? 0.85 + Math.sin(state.clock.elapsedTime * 4) * 0.15
      : 0.8;
  });
  return (
    <mesh
      ref={ref}
      position={[position[0], Y.pad, position[2]]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(evShim(e)); } : undefined}
      onPointerOver={onClick ? cursor.over : undefined}
      onPointerOut={onClick ? cursor.out : undefined}
    >
      <planeGeometry args={[CARD.W * 1.12, CARD.H * 1.12]} />
      <meshStandardMaterial map={riftTexture(label)} transparent opacity={0.8} />
    </mesh>
  );
}

// ── Count badge (GM chrome, screen-space, never eats clicks) ──
function Badge({ position, children, tone = GM.vellumDim }) {
  return (
    <Html position={position} center zIndexRange={[10, 4]} style={{ pointerEvents: "none" }}>
      <div style={{
        background: "rgba(7,4,16,0.82)", border: `1px solid ${GM.inkGold}44`,
        borderRadius: 4, padding: "1px 7px", whiteSpace: "nowrap",
        fontFamily: GM.font, fontSize: 10, letterSpacing: 1, color: tone,
        textShadow: "0 1px 2px #000",
      }}>{children}</div>
    </Html>
  );
}

// ── Pile — deck / necropolis. One box scaled by count (never N meshes). ──
function Pile({ position, count, label, topFaceUpCardId = null, onClick,
                glow = false, scatterSeed = null, topTier = null, dread = 0,
                pulse = null }) {
  // `pulse` is a plain colour string. Card3D already knows how to
  // breathe on one (SHIP 3's targeting pulse), so the draw cue costs
  // this renderer exactly one forwarded prop and no new machinery.
  // SHIP 6 — THE DREAD. The 2D board scales its Necropolis glow with the
  // population (necroPulseFaint → Mid → Strong). Here the slab itself
  // breathes violet, harder the fuller it gets. `dread` is 0..1.
  // ⚠ HOOK ORDER: this useFrame MUST sit above the `count <= 0` early
  // return — a hook after a conditional return is a rules-of-hooks
  // violation that only fires when the pile empties mid-duel.
  const bodyRef = useRef();
  useFrame((state) => {
    const m = bodyRef.current && bodyRef.current.material;
    if (!m) return;
    const want = dread > 0
      ? (0.10 + dread * 0.55) * (0.72 + Math.sin(state.clock.elapsedTime * (1.1 + dread * 1.9)) * 0.28)
      : 0;
    m.emissiveIntensity += (want - m.emissiveIntensity) * 0.1;
  });
  if (count <= 0) return <Rift position={position} label={label} onClick={onClick} />;
  const h = Math.max(CARD.T, Math.min(count, 40) * CARD.T * 0.6);
  const topY = Y.card + h;
  const R = scatterSeed != null ? rnd(seedOf(scatterSeed) + count) : null;
  const jitter = R ? (R() - 0.5) * 0.14 : 0;
  return (
    <group position={[position[0], 0, position[2]]} rotation={[0, jitter, 0]}>
      <mesh ref={bodyRef} position={[0, Y.card + h / 2, 0]} castShadow
        onClick={onClick ? (e) => { e.stopPropagation(); onClick(evShim(e)); } : undefined}
        onPointerOver={onClick ? cursor.over : undefined}
        onPointerOut={onClick ? cursor.out : undefined}
      >
        <boxGeometry args={[CARD.W, h, CARD.H]} />
        <meshStandardMaterial color="#151030" roughness={0.75}
          emissive={SD.purple} emissiveIntensity={0} />
      </mesh>
      <Card3D
        cardId={topFaceUpCardId}
        faceDown={!topFaceUpCardId}
        // SHIP 8.6 — the pile's top card is WebGL too. A DOM face paints
        // over every 3D thing behind it in SCREEN space, and the Necropolis
        // sits beside the Mind rift: its top card was covering the being
        // (or essence) standing there from the table camera.
        noDomFace={!!topFaceUpCardId}
        position={[0, topY + CARD.T / 2 + 0.002, 0]}
        glow={glow}
        pulse={pulse}
        tier={topFaceUpCardId ? topTier : null}
        onClick={onClick}
      />
      <Badge position={[0, topY + 0.45, 0]}>{label} · {count}</Badge>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SHIP 3 — MOTION. The renderer stays pure: every animation below is
// DERIVED from a state transition (prev props vs current props), the
// same pattern as the 2D revival detector. No game logic, no events —
// the field simply notices what changed and makes it physical.
// ═══════════════════════════════════════════════════════════════════
const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

// Seat-space → world-space (opponent's group is π about Y).
const sp = (isMe, [x, y, z]) => (isMe ? [x, y, z] : [-x, y, -z]);

const escWorld = (isMe, i) =>
  sp(isMe, [SEAT.esclings[i][0], Y.card + CARD.T * 1.5 + Y.stackGap, SEAT.esclings[i][2]]);
const tomeWorld = (isMe, i) =>
  sp(isMe, [SEAT.tomes[i][0], Y.card + CARD.T / 2, SEAT.tomes[i][2]]);
const pileWorld = (isMe, pos, count) =>
  sp(isMe, [pos[0], Y.card + Math.max(CARD.T, Math.min(count, 40) * CARD.T * 0.6) + CARD.T, pos[2]]);
const oppHandWorld = () => sp(false, [0, Y.card + 0.25, SEAT.handZ - 0.6]);
const myHandWorld = (i, n) => { const p = fanPose(i, n); return [p.x, p.y, p.z]; };
const plateWorld = (isMe) => sp(isMe, [SEAT.plate[0], SEAT.plate[1] + 0.45, SEAT.plate[2]]);
// H21 — the two rifts the Ship 3 detector never watched, plus the
// Soul. Without these three, five of the eight arrivals in the Strike
// Language would be beautifully specified and never once fire: a Curio
// and a Prophecy live in their own rifts, and an Impera / U'hlti /
// Affinity never touches a rift at all — it attaches to a being's SOUL.
const curioWorld = (isMe, i) =>
  sp(isMe, [SEAT.curios[i][0], Y.card + CARD.T / 2, SEAT.curios[i][2]]);
const prophWorld = (isMe) =>
  sp(isMe, [SEAT.prophecy[0], Y.card + CARD.T / 2, SEAT.prophecy[2]]);

// Multiset difference: ids in a that aren't in b (respecting counts).
const multisetDiff = (a, b) => {
  const counts = {};
  (b || []).forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
  const out = [];
  (a || []).forEach((id) => {
    if (counts[id] > 0) counts[id]--;
    else out.push(id);
  });
  return out;
};

// Keyframe trap law: NEVER inline <style> keyframes in JSX — inject
// imperatively with an id-guard on document.head.
function ensureSf3dKeyframes() {
  if (typeof document === "undefined" || document.getElementById("sf3d-keyframes")) return;
  const el = document.createElement("style");
  el.id = "sf3d-keyframes";
  el.textContent =
    "@keyframes sf3dRise { 0% { transform: translateY(0); opacity: 1; } " +
    "100% { transform: translateY(-46px); opacity: 0; } }" +
    // H21 — the arrival sigil: rises out of the card and dissolves.
    "@keyframes sf3dSigil { 0% { transform: translateY(6px) scale(0.5); opacity: 0; } " +
    "25% { transform: translateY(0) scale(1.15); opacity: 1; } " +
    "60% { transform: translateY(-10px) scale(1); opacity: 0.9; } " +
    "100% { transform: translateY(-30px) scale(0.94); opacity: 0; } }";
  document.head.appendChild(el);
}

// ── THE ARRIVAL (H21b) ─────────────────────────────────────────────
// One airborne card. Until today every card in the game crossed the
// table on the SAME ballistic hop — an Escling, a Prophecy and a
// Lord's U'hlti all flew identically, which meant Terror's whole
// taxonomy was invisible in motion. Now the flight reads its profile
// from the Strike Language and the PATH itself changes:
//
//   arc      ballistic hop — the honest default for a body
//   drop     straight down from above; no lateral travel at all
//   manifest NO travel — it appears at the rift and swells into being
//   rise     comes up slowly from beneath the felt
//   skim     low and flat, set down rather than summoned
//   still    does not move; the light comes to IT
//
// The landing squash, the mid-air flip and the yaw all still ride the
// same timeline they always did — this only re-routes the position.
function FlightCard({ flight, onDone }) {
  const ref = useRef();
  const st = useRef({ t: 0, done: false });
  const style = flight.style || "arc";
  useFrame((_, dt) => {
    const s = st.current;
    if (s.done || !ref.current) return;
    s.t = Math.min(1, s.t + Math.min(dt, 0.05) * (flight.speed || 1.9));
    const e = easeInOutQuad(s.t);
    const [fx0, fy0, fz0] = flight.from;
    const [tx, ty, tz] = flight.to;
    const arc = flight.arc ?? 1.2;

    let x, y, z;
    if (style === "manifest" || style === "still") {
      // Spellwork does not cross a table. It is simply THERE, and then
      // it is more there. Position is the destination for the whole
      // timeline; scale and flare carry the entire arrival.
      x = tx; y = ty; z = tz;
    } else if (style === "drop") {
      // A brand comes straight down. Lateral is resolved instantly so
      // there is no travel to watch — only the fall.
      x = tx; z = tz;
      y = ty + (1 - e) * (1.9 + arc);
    } else if (style === "rise") {
      // Up through the felt, unhurried.
      x = tx; z = tz;
      y = ty - (1 - e) * 0.85 + Math.sin(s.t * Math.PI) * arc;
    } else if (style === "skim") {
      // Set down by a hand: it crosses, but it barely leaves the table.
      x = fx0 + (tx - fx0) * e;
      y = fy0 + (ty - fy0) * e + Math.sin(s.t * Math.PI) * arc;
      z = fz0 + (tz - fz0) * e;
    } else {
      x = fx0 + (tx - fx0) * e;
      y = fy0 + (ty - fy0) * e + Math.sin(s.t * Math.PI) * arc;
      z = fz0 + (tz - fz0) * e;
    }
    ref.current.position.set(x, y, z);

    // Yaw: the owner's orientation, plus the profile's optional spin.
    // A Casting turns as it resolves; a Descent never does.
    ref.current.rotation.y = (flight.yaw || 0) + (flight.spin || 0) * (1 - e) * Math.PI;
    // Tilt: how far the card leans through its own travel. A Descent
    // pitches forward into the felt; an Offering lays back.
    ref.current.rotation.x = (flight.tilt || 0) * Math.sin(s.t * Math.PI);
    if (flight.flip) ref.current.rotation.z = Math.PI * (1 - e);
    else ref.current.rotation.z = flight.faceDownLand ? Math.PI : 0;

    // Scale. A manifest swells from nothing with a slight overshoot; a
    // still declaration only breathes; everything else keeps the
    // original landing squash, which is what makes a card LAND.
    if (style === "manifest") {
      const k = s.t < 0.7 ? (s.t / 0.7) : 1 + (1 - (s.t - 0.7) / 0.3) * 0.12;
      const g = Math.max(0.04, Math.min(1.12, k));
      ref.current.scale.set(g, g, g);
    } else if (style === "still") {
      const g = 1 + Math.sin(s.t * Math.PI) * 0.05;
      ref.current.scale.set(g, 1, g);
    } else {
      const squash = s.t > 0.9 ? 0.85 : 1; // the card LANDS
      ref.current.scale.set(1, squash, 1);
    }
    if (s.t >= 1) { s.done = true; onDone(flight); }
  });
  return (
    <group ref={ref} position={flight.from}>
      {/* SHIP 5 — a foil does not stop being a foil mid-air. The tier
          was stamped on the flight when it launched. */}
      <Card3D cardId={flight.cardId} faceDown={flight.hidden} position={[0, 0, 0]}
        tier={flight.tier || null} />
    </group>
  );
}

// Rising damage/heal number anchored to a world point (GM chrome).
function FloatNumber({ float }) {
  return (
    <Html position={float.pos} center zIndexRange={[30, 20]} style={{ pointerEvents: "none" }}>
      <div style={{
        animation: "sf3dRise 1.15s ease-out forwards",
        fontFamily: GM.font, fontWeight: 900, fontSize: 20,
        color: float.color, letterSpacing: 1,
        textShadow: "0 0 8px rgba(0,0,0,0.9), 0 2px 3px #000",
        whiteSpace: "nowrap",
      }}>{float.text}</div>
    </Html>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SHIP 6 — THE BELLS AND THE WHISTLES
// Everything below is DERIVED from a state transition or from time.
// Nothing here reads Firebase, decides a rule, or calls a handler.
// ═══════════════════════════════════════════════════════════════════

// ── WORLD SHAKE ────────────────────────────────────────────────────
// DELETE THE DEGREE OF FREEDOM: the obvious version jitters the camera,
// which puts this code in a tug-of-war with OrbitControls AND with
// CameraRig's lerp — three writers on one transform, which is three
// bugs. Instead the WORLD moves and the lens holds still. Every CSS3D
// face copies its host mesh's matrixWorld, so the DOM card faces
// inherit the shake for free. Sinusoidal, never Math.random() — the
// PRNG law covers render paths, and two incommensurate sines read as
// noise anyway.
function WorldShake({ children }) {
  const ref = useRef();
  useFrame((state) => {
    const g = ref.current;
    if (!g) return;
    const now = Date.now();
    if (now < FX_BUS.shakeUntil) {
      const k = (FX_BUS.shakeUntil - now) / (FX_BUS.shakeSpan || 1);
      const m = FX_BUS.shakeMag * k * k; // quadratic decay — a hit, not a wobble
      const t = state.clock.elapsedTime;
      g.position.set(
        Math.sin(t * 71.3) * m,
        Math.sin(t * 53.7) * m * 0.55,
        Math.cos(t * 61.1) * m
      );
    } else if (g.position.lengthSq() !== 0) {
      g.position.set(0, 0, 0);
    }
  });
  return <group ref={ref}>{children}</group>;
}

// ── MOTES ──────────────────────────────────────────────────────────
// The 2D field has sf-moteRise; the world gets the real thing. One
// BufferGeometry, one draw call, positions nudged in place — never N
// meshes and never setState. Seeded, so the dust is identical on every
// client forever.
const MOTE_N = 90;
function Motes() {
  const geo = useMemo(() => {
    const R = rnd(seedOf("shatterfield-motes"));
    const pos = new Float32Array(MOTE_N * 3);
    const spd = new Float32Array(MOTE_N);
    for (let i = 0; i < MOTE_N; i++) {
      pos[i * 3] = (R() - 0.5) * TABLE.W;
      pos[i * 3 + 1] = R() * 4.2;
      pos[i * 3 + 2] = (R() - 0.5) * TABLE.D;
      spd[i] = 0.10 + R() * 0.26;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.userData.spd = spd;
    return g;
  }, []);
  useEffect(() => () => geo.dispose(), [geo]);
  useFrame((state, dt) => {
    const p = geo.attributes.position.array;
    const spd = geo.userData.spd;
    const t = state.clock.elapsedTime;
    const step = Math.min(dt, 0.05); // tab-return guard: never a 4s jump
    for (let i = 0; i < MOTE_N; i++) {
      p[i * 3 + 1] += spd[i] * step;
      p[i * 3] += Math.sin(t * 0.4 + i) * 0.0006;
      if (p[i * 3 + 1] > 4.4) p[i * 3 + 1] = 0.05;
    }
    geo.attributes.position.needsUpdate = true;
  });
  return (
    <points geometry={geo}>
      <pointsMaterial size={0.05} color={SD.goldBright} transparent opacity={0.30}
        sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending}
        toneMapped={false} />
    </points>
  );
}

// ── TURN SWEEP ─────────────────────────────────────────────────────
// A band of light crosses the felt on every turn change — the 3D answer
// to the 2D seam glint. Mounted once, invisible until FX_BUS says go.
function TurnSweep() {
  const ref = useRef();
  const st = useRef({ t: 2, last: 0 });
  useFrame((_, dt) => {
    const m = ref.current;
    if (!m) return;
    if (FX_BUS.sweepAt !== st.current.last) { st.current.last = FX_BUS.sweepAt; st.current.t = 0; }
    if (st.current.t >= 1) { if (m.visible) m.visible = false; return; }
    m.visible = true;
    st.current.t = Math.min(1, st.current.t + Math.min(dt, 0.05) * 0.9);
    const e = st.current.t;
    m.position.z = -TABLE.D / 2 - 1 + e * (TABLE.D + 2);
    m.material.opacity = Math.sin(e * Math.PI) * 0.45;
  });
  return (
    <mesh ref={ref} visible={false} rotation={[-Math.PI / 2, 0, 0]}
      position={[0, Y.seam + 0.002, 0]}>
      <planeGeometry args={[TABLE.W, 1.5]} />
      <meshBasicMaterial color={SD.goldBright} transparent opacity={0}
        depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
    </mesh>
  );
}

// ── LANDING RING ───────────────────────────────────────────────────
// Every arrival stamps a ring on the felt. Tiny, cheap, and it's what
// makes a card feel like it ARRIVED rather than teleported.
// Self-retiring: it calls onDone and the parent drops it.
//
// H21 — the ring now reads its profile instead of one hardcoded
// expansion. r0 → r1 over `life` seconds, so a Descent stamps wide and
// fast, a Declaration opens slow and enormous, and the Affinity —
// whose profile sets r0 > r1 — CONTRACTS. That is deliberate: the top
// of the attachment ladder is denial, not damage, so the Sealing
// closes an iris instead of radiating a shockwave. One sampler, both
// directions, no branch.
function LandingRing({ ring, onDone }) {
  const ref = useRef();
  const st = useRef({ t: 0, done: false });
  const r0 = ring.r0 ?? 0.3;
  const r1 = ring.r1 ?? 1.55;
  const rate = 1 / Math.max(0.2, ring.life ?? 0.6);
  useFrame((_, dt) => {
    const s = st.current;
    if (s.done || !ref.current) return;
    s.t = Math.min(1, s.t + Math.min(dt, 0.05) * rate);
    const k = r0 + (r1 - r0) * s.t;
    ref.current.scale.set(k, k, 1);
    ref.current.material.opacity = (1 - s.t) * (ring.peak ?? 0.7);
    if (s.t >= 1) { s.done = true; onDone(ring.id); }
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}
      position={[ring.pos[0], Y.seam + 0.004, ring.pos[2]]}>
      <ringGeometry args={[0.44, 0.54, 48]} />
      <meshBasicMaterial color={ring.color || SD.goldBright} transparent opacity={0.7}
        side={THREE.DoubleSide} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

// ── THE SIGIL (H21b) ───────────────────────────────────────────────
// One glyph blooms over a card as it lands, in the profile's own hue.
// This is the piece that makes eight Tome schools LEGIBLE — without
// it, a Nihil counter and a Ritual resurrection are two shades of the
// same flash. A billboard, exactly like FloatNumber: it always faces
// the camera and reads right-side-up over the opponent's π-rotated
// seat for free.
function ArrivalSigil({ sigil }) {
  return (
    <Html position={sigil.pos} center zIndexRange={[29, 20]} style={{ pointerEvents: "none" }}>
      <div style={{
        animation: "sf3dSigil 0.95s cubic-bezier(0.2,0.8,0.3,1) forwards",
        fontFamily: GM.font, fontWeight: 900, fontSize: sigil.size || 34,
        color: sigil.color, lineHeight: 1,
        textShadow: `0 0 14px ${sigil.color}, 0 0 30px ${sigil.color}88, 0 2px 4px rgba(0,0,0,0.95)`,
        whiteSpace: "nowrap",
      }}>{sigil.glyph}</div>
    </Html>
  );
}

// ── SHATTER ────────────────────────────────────────────────────────
// A being that falls to the Necropolis doesn't just slide off the
// board — the Shatterfield shatters it. Seeded shards on a ballistic
// arc, fading as they fall. (The 2D board spends sf-cardShatter +
// sf-shatterCracks on this moment; it earns the same weight here.)
const SHARD_N = 14;
function Shatter({ burst, onDone }) {
  const grp = useRef();
  const st = useRef({ t: 0, done: false });
  const shards = useMemo(() => {
    const R = rnd(seedOf(burst.seed || burst.id));
    return Array.from({ length: SHARD_N }, () => {
      const a = R() * Math.PI * 2;
      const out = 0.8 + R() * 1.5;
      return {
        vx: Math.cos(a) * out, vz: Math.sin(a) * out,
        vy: 1.4 + R() * 1.9,
        rx: (R() - 0.5) * 8, rz: (R() - 0.5) * 8,
        s: 0.055 + R() * 0.095,
      };
    });
  }, [burst.id, burst.seed]);
  useFrame((_, dt) => {
    const s = st.current;
    if (s.done || !grp.current) return;
    s.t = Math.min(1, s.t + Math.min(dt, 0.05) * 1.25);
    const t = s.t;
    grp.current.children.forEach((m, i) => {
      const sh = shards[i];
      if (!sh) return;
      m.position.set(sh.vx * t, sh.vy * t - 4.4 * t * t, sh.vz * t);
      m.rotation.x = sh.rx * t;
      m.rotation.z = sh.rz * t;
      if (m.material) m.material.opacity = Math.max(0, 1 - t * 1.15);
    });
    if (s.t >= 1) { s.done = true; onDone(burst.id); }
  });
  return (
    <group ref={grp} position={burst.pos}>
      {shards.map((sh, i) => (
        <mesh key={i} scale={[1, 1, 1]}>
          <tetrahedronGeometry args={[sh.s, 0]} />
          <meshStandardMaterial color={burst.color || "#c8506a"}
            emissive={burst.color || "#c8506a"} emissiveIntensity={1.3}
            transparent opacity={1} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ── DROP HINT ──────────────────────────────────────────────────────
// Which empty rifts answer the card currently held? Derived from STATIC
// card data only (type), never from a rules call — the renderer hints,
// the enforcer decides. An illegal tap still bounces off the same gate
// it always did; this only lights the neighbourhood.
const BEING_TYPES = new Set(["Escendant", "Impera", "U'hlti", "Affinity"]);
const dropHintFor = (cardId) => {
  const c = getCard(cardId);
  if (!c) return null;
  if (BEING_TYPES.has(c.type)) return "esc";
  if (c.type === "Tome") return "tome";
  if (c.type === "Curio") return "curio";
  return null;
};

// ═══════════════════════════════════════════════════════════════════
// C18 — THE PARITY LAW: THE GATE LEDGER  (audit walked July 25, 2026)
// ═══════════════════════════════════════════════════════════════════
// Every interactive surface this renderer exposes, beside the 2D gate it
// must be character-identical to. Line numbers are Shatterfield.js.
// STANDING RULE: a new interactive element ships with its 2D gate copied
// verbatim and cited here, in the same commit. If 2D has no equivalent,
// say so and say why — an unlisted click is an unaudited click.
//
//  SURFACE              2D REF   2D GATE                          3D
//  escling rift (empty) 2898     onSlotAction(i,e) — both seats    ✔ same
//  escling card         2896     onSlotAction(i,e) — unconditional ✔ same
//  escendant card       2894     onSlotAction(i,e) — unconditional ✔ same
//  tome rift (empty)    2977     onTomeAction("tome",i,e) — both   ✔ same
//  tome card            2977     onTomeAction("tome",i,e)          ✔ same
//  curio rift (empty)   2967/84  NO onClick on either seat         ✔ fixed 7/25
//  curio card           2967/84  onTomeAction("curio",i,e)         ✔ same
//  prophecy rift(empty) 2856     EmptyRift, NO onClick             ✔ fixed 7/25
//  prophecy card        2855     onTomeAction("prophecy",0,e)      ✔ same
//  necropolis           2929     onNecropolisAction — uncond, both ✔ same
//                                (2D keeps the click when empty; the
//                                 count only changes the cursor)
//  grimoire pile        3062     isMe && canDraw                   ✔ fixed 7/24
//  luxmoire pile        3003     isMe && canDraw                   ✔ fixed 7/25
//  primera stamp        2932     isMe && stampsFor(slot,i)         ✔ same
//  exile pit (8.4)      4078     isMe && onExileAction             ✔ same (2026-09-15)
//                                (adversary's tile: no onClick; 3D pit idem)
//                                (H22c — the seal is DOM on the ONE
//                                 FACE, so the gate is the same object
//                                 the 2D card gets: `stampsFor` returns
//                                 null for the adversary's seat and for
//                                 anything that isn't an Escendant, and
//                                 carries its own ready/spent/locked
//                                 state. A locked seal stays TAPPABLE on
//                                 purpose — the PRIMERA gate's own reason
//                                 is the error message, both boards.)
//  hand card            3029     onHandCardClick(i,e) — uncond     ✔ same
//  opp hand             —        no handler passed                 ✔ none
//  opp draw piles       3003/62  isMe false ⇒ dead                 ✔ none
//  seat placard         —        3D-only, pointerEvents:"none"     ✔ inert
//  camera presets       —        3D-only chrome, no game action    ✔ n/a
//  background           —        onPointerMissed = cursor only     ✔ inert
//
// KNOWN VARIANCE, deliberate (not a gate break — no permission differs):
//  • Drag-to-play. The 2D rifts carry onDragOver/onDrop (2883-84, 2954-55)
//    behind an `isDropOk` test; HTML5 drag has no meaning over a WebGL
//    canvas. The CLICK path to play a card is present and identical in
//    both boards, so nothing is reachable in 2D that is unreachable here
//    — it is a lost convenience on desktop, not a lost move. A 3D-native
//    pointer-drag (raycast the felt, drop on the hovered rift) is the
//    honest port when the icing arc wants it.
//  • ProphecyButton lives inside 2D <PlayerSide> on desktop; the 3D board
//    gets it from the floating chrome instead (Shatterfield.js, gated
//    `field3D && myTurn && prophecy` — the identical PlayerSide gate).
// ═══════════════════════════════════════════════════════════════════

// ── One seat: rifts, piles, wings, name plate ──
function SeatSide({ player, isMe, targetMode, attackingSlot,
                    onSlot, onTome, onNecropolis,
                    onDrawGrimoire, onDrawLuxmoire, canDraw, myTurn,
                    otherField = null, effLyt = null, effLyf = null,
                    seatKey = "m", suppressed = {}, fxShakes = {}, fxPulses = {},
                    fxStrikes = {}, fxImpacts = {},
                    holoOf = null, dropHint = null,
                    stampsFor = null, onSealPress = null,
                    // ── THE DRAW PULSE — colour strings or null, decided by
                    // Shatterfield.js. C18 parity: the 2D tiles remain the law
                    // on WHEN, and only the LOOK differs between surfaces.
                    luxPulse = null, grimPulse = null,
                    luxTone = null, grimTone = null,
                    // SHIP 8.4 — the Exile pit's click (my seat only; see the ledger)
                    onExile = null }) {
  // ⬡ SHIP 5 — which foil does this copy wear? The resolver is passed
  // in from Shatterfield.js (the renderer owns no collection data, and
  // never asks Firebase anything). It arrives NULL for the adversary's
  // seat — same law as the 2D board, which hands their <PlayerSide> a
  // null collection: you see your own foils, never theirs.
  const tOf = (id) => {
    if (!holoOf || !id) return null;
    try { return holoOf(id) || null; } catch (_) { return null; }
  };
  const field = player?.field || {};
  // ESCLING_SLOTS, never a literal. riftArray() fills to the length it is
  // given, and Firebase drops null holes — a board with only rifts 0 and 3
  // occupied arrives as the object {0:…, 3:…}, so normalizing it to length 3
  // would drop rift 3's being entirely, with no error anywhere to say so.
  const esclings = riftArray(field.esclings, ESCLING_SLOTS);
  const tomes = riftArray(field.tomes, 3);
  const curios = riftArray(field.curios, 2);
  const prophecy = field.prophecy || player?.prophecy || null;
  const necropolis = Array.isArray(player?.necropolis) ? player.necropolis : [];
  // ⚠ C18 (July 25): the 2D board lights opp beings in BOTH attack modes —
  // isTarget = !isMe && (targetMode === "select" || targetMode === "escling").
  // "escling" is the beings-only mode (esclings can strike beings, never
  // Anima); this pulse was missing it, so an escling attack gave zero
  // target feedback on the 3D board. Identical disjunction now.
  const oppTargetable = !isMe && (targetMode === "select" || targetMode === "escling");

  return (
    <group>
      {/* ── Escling rifts (front row) ── */}
      {SEAT.esclings.map((pos, i) => {
        const slot = esclings[i];
        const click = onSlot ? (ev) => onSlot(i, ev) : undefined;
        if (!slot?.escling && !slot?.escendant) {
          return <Rift key={`esc${i}`} position={pos} label="RIFT" onClick={click}
                       highlight={isMe && dropHint === "esc"} />;
        }
        const isAttacker = isMe && attackingSlot === i;
        const soulN = (slot?.soul || []).length;
        const beingCard = slot?.escendant ? getCard(slot.escendant) : null;
        // SHIP 2 — effective stats from Shatterfield's own pure helpers
        // (passed as props: the renderer never re-derives game logic).
        let lytShow = beingCard?.lyt ?? null;
        let lyfShow = slot?.currentLyf ?? beingCard?.lyf ?? null;
        if (slot?.escendant && typeof effLyt === "function") {
          try { lytShow = effLyt(slot, field, otherField); } catch (_) {}
        }
        if (slot?.escendant && slot?.currentLyf == null && typeof effLyf === "function") {
          try { lyfShow = effLyf(slot, field); } catch (_) {}
        }
        // Ship 4.2 — the gems are unconditional. Art or no art, a being on
        // the field always wears its live LYF/LYT.
        const statOverlay = slot?.escendant ? (
          <StatOverlay cardId={slot.escendant} lytShow={lytShow} lyfShow={lyfShow} />
        ) : null;
        // ── H22c — THE SEALS ──────────────────────────────────────
        // Identical call to the 2D card's (Shatterfield.js 2932): my seat
        // only, Escendants only, null everywhere else. The renderer holds
        // no Primera rules — `stampsFor` arrives pre-judged (ready /
        // spent / locked) and every press lands in the same
        // `handlePressPrimera` chokepoint the 2D board uses.
        // Each READY press is wrapped once so the WORLD answers too: a
        // gold ring stamps the felt under the being. A locked press gets
        // no ring on purpose — that press is an error message, not an
        // event (H20's one-event-one-channel discipline).
        const seals = (isMe && stampsFor && slot?.escendant)
          ? (() => {
              const s = stampsFor(slot, i);
              if (!s || !onSealPress) return s || null;
              const wrap = (st) => (st && st.onPress ? {
                ...st,
                onPress: () => {
                  if (st.state === "ready") {
                    onSealPress(pos, st.color);
                    // SHIP 8.3 — the being hops as its seal is pressed
                    beingLife.kick(`${seatKey}:esc:${i}`, "stamp");
                  }
                  st.onPress();
                },
              } : st);
              return { ilk: wrap(s.ilk), school: wrap(s.school) };
            })()
          : null;
        // SHIP 5 — STACK LAW: a foil escling is a thicker escling, so
        // the escendant on top of it has to sit on the REAL top face,
        // not on an assumed base thickness. This is the one place in
        // the file where one card's rarity moves another card.
        const esclingTier = tOf(slot?.escling);
        const esclingT = holoThickness(esclingTier);
        // ── SHIP 8 — THE STAND ──────────────────────────────────
        // An Escendant with a body in the kit stands on its card. The
        // whole rift stack (Escling under Escendant) drops its CSS3D
        // decals for the WebGL art path, or the Escling's face would
        // paint over the slab AND the body above it — DOM does not
        // depth-test against the canvas. Beings the kit does not know
        // (none today; the 36 cards are the 36 bodies) keep the decal
        // and render exactly as Ship 7 did.
        const stands = !!(slot?.escendant && beingOf(slot.escendant));
        // SHIP 8.3 — the body takes the fight off the card: the SAME fx
        // entries the card reads (strike, impact, shake, the lunge) go to
        // the rider, and Card3D stops moving for them while one is riding.
        const rider = stands ? (
          <FieldBeing cardId={slot.escendant} seed={(isMe ? 0 : 7) + i * 1.9}
                      stance={isMe ? "mine" : "theirs"} lifeKey={`${seatKey}:esc:${i}`}
                      strike={fxStrikes[`${seatKey}:esc:${i}`] || null}
                      impact={fxImpacts[`${seatKey}:esc:${i}`] || null}
                      shakeUntil={fxShakes[`${seatKey}:esc:${i}`] || 0}
                      lunge={isAttacker}
                      fallen={!!slot?.fallen}
                      fractured={lyfShow != null && lyfShow <= 0} />
        ) : null;
        return (
          <group key={`esc${i}`}>
            <Rift position={pos} label="RIFT" onClick={click} />
            {slot?.escling && (
              <Card3D
                cardId={slot.escling}
                position={[pos[0], Y.card + CARD.T / 2, pos[2]]}
                tier={esclingTier}
                noDomFace={stands || (!slot?.escendant && hasProp(slot.escling))}
                // SHIP 8.6 — the ESSENCE: an Escling with no being made of
                // it yet shows its school's raw stuff on the card.
                rider={!slot?.escendant && hasProp(slot.escling)
                  ? <FieldProp cardId={slot.escling} seed={(isMe ? 0 : 5) + i * 1.3} stance={isMe ? "mine" : "theirs"} /> : null}
                onClick={click}
              />
            )}
            {slot?.escendant && !suppressed[`${seatKey}:esc:${i}`] && (
              <Card3D
                cardId={slot.escendant}
                tier={tOf(slot.escendant)}
                rider={rider}
                noDomFace={stands}
                position={[pos[0],
                  Y.card + (slot?.escling ? esclingT : CARD.T) + Y.stackGap + CARD.T / 2,
                  pos[2]]}
                rotation={[0, (rnd(seedOf(slot.escendant) + i)() - 0.5) * 0.07, 0]}
                breathe
                lunge={isAttacker}
                glow={isAttacker}
                pulse={oppTargetable ? SD.red : (isAttacker ? SD.gold : (fxPulses[`${seatKey}:esc:${i}`] || null))}
                shakeUntil={fxShakes[`${seatKey}:esc:${i}`] || 0}
                impact={fxImpacts[`${seatKey}:esc:${i}`] || null}
                strike={fxStrikes[`${seatKey}:esc:${i}`] || null}
                overlay={statOverlay}
                stamps={seals}
                onClick={click}
              />
            )}
            {slot?.escendant && (soulN > 0 || slot?.deviant) && (
              <Badge position={[pos[0], 0.12, pos[2] + CARD.H / 2 + 0.24]}
                     tone={GM.vellum}>
                {soulN ? `◈ ${soulN}` : ""}{soulN && slot?.deviant ? " · " : ""}{slot?.deviant ? "⛧ Deviant" : ""}
              </Badge>
            )}
          </group>
        );
      })}

      {/* ── Tome rifts (back row) ── */}
      {SEAT.tomes.map((pos, i) => {
        const { id, faceDown } = tomeInfo(tomes[i]);
        const click = onTome ? (ev) => onTome("tome", i, ev) : undefined;
        if (!id || suppressed[`${seatKey}:tome:${i}`]) {
          return <Rift key={`tome${i}`} position={pos} label="TOME" onClick={click}
                       highlight={isMe && !id && dropHint === "tome"} />;
        }
        // SHIP 8.4 — a Treasure, Construction or Rite stands on its card
        // as a THING; face-down sets stay cards (nothing about a set
        // Tome may be read across the seam, a shape included).
        const propped = !faceDown && hasProp(id);
        return (
          <Card3D key={`tome${i}`} cardId={id} faceDown={faceDown}
            tier={faceDown ? null : tOf(id)}
            position={[pos[0], Y.card + CARD.T / 2, pos[2]]}
            noDomFace={propped}
            rider={propped ? <FieldProp cardId={id} seed={(isMe ? 0 : 3) + i} stance={isMe ? "mine" : "theirs"} lifeKey={`${seatKey}:tome:${i}`} /> : null}
            onClick={click} />
        );
      })}

      {/* ── Curios ── */}
      {/* ⚠ C18 (July 25): an EMPTY curio rift is NOT clickable — the 2D
          board renders it with no onClick on either seat, because Rulez v2
          pre-places Curios at duel setup (no play-from-hand path). The 3D
          click was reaching handleTomeAction's legacy place-from-hand
          branch, a path the 2D board sealed. Filled curios stay clickable. */}
      {SEAT.curios.map((pos, i) => {
        const cid = typeof curios[i] === "object" && curios[i] !== null ? curios[i]?.id : curios[i];
        const click = onTome ? (ev) => onTome("curio", i, ev) : undefined;
        // H21 — while the Offering is still in the air the seated copy
        // stays hidden, or the card renders twice. Same suppression the
        // escendant and tome rifts have used since Ship 3.
        if (!cid || suppressed[`${seatKey}:curio:${i}`]) {
          return <Rift key={`cur${i}`} position={pos} label="CURIO" />;
        }
        return (
          <Card3D key={`cur${i}`} cardId={cid} tier={tOf(cid)}
            position={[pos[0], Y.card + CARD.T / 2, pos[2]]}
            noDomFace={hasProp(cid)}
            rider={hasProp(cid) ? <FieldProp cardId={cid} seed={(isMe ? 0 : 3) + i * 2.2} stance={isMe ? "mine" : "theirs"} /> : null}
            onClick={click} />
        );
      })}

      {/* ── Prophecy ── */}
      {/* ⚠ C18 (July 25): the EMPTY prophecy rift is NOT clickable — the
          2D board renders <EmptyRift highlight={false}/> with no onClick on
          either seat. Only a seated Prophecy card answers a tap (view). */}
      {prophecy && !suppressed[`${seatKey}:proph`] ? (
        <Card3D cardId={prophecy} tier={tOf(prophecy)}
          position={[SEAT.prophecy[0], Y.card + CARD.T / 2, SEAT.prophecy[2]]}
          noDomFace={hasProp(prophecy)}
          rider={hasProp(prophecy) ? <FieldProp cardId={prophecy} seed={isMe ? 1 : 9} stance={isMe ? "mine" : "theirs"} /> : null}
          onClick={onTome ? (ev) => onTome("prophecy", 0, ev) : undefined} />
      ) : (
        <Rift position={SEAT.prophecy} label="PROPHECY" />
      )}

      {/* ── SHIP 8.5 — the cellar the Necropolis goes down into ── */}
      <CellarDoor position={SEAT.necropolis} dread={Math.min(1, necropolis.length / 12)} />
      {/* ── SHIP 8.4 — THE EXILE PIT (R112). Gate copied from the 2D tile:
          Shatterfield.js 4078  onClick={isMe && onExileAction ? (e) => {
          e.stopPropagation(); onExileAction(e); } : undefined} — my seat
          only, the adversary's pit is view-only. The count badge is the
          2D tile's own number. */}
      <Rift position={SEAT.exile} label="EXILE" />
      <ExilePit position={SEAT.exile} count={(player?.exile || []).length}
        onClick={isMe && onExile ? (e) => { e.stopPropagation(); onExile(evShim(e)); } : undefined}
        onPointerOver={isMe && onExile ? cursor.over : undefined}
        onPointerOut={isMe && onExile ? cursor.out : undefined} />
      <Badge position={[SEAT.exile[0], 0.14, SEAT.exile[2] + 0.62]} tone="#8b6bd6">
        {`🕳 ${(player?.exile || []).length}`}
      </Badge>
      {/* ── Necropolis (top card face-up, seeded scatter) ── */}
      <Pile position={SEAT.necropolis} count={necropolis.length} label="NECRO"
        topFaceUpCardId={
          suppressed[`${seatKey}:necroTop`]
            ? (necropolis[necropolis.length - 2] || null)
            : (necropolis[necropolis.length - 1] || null)
        }
        topTier={tOf(
          suppressed[`${seatKey}:necroTop`]
            ? necropolis[necropolis.length - 2]
            : necropolis[necropolis.length - 1]
        )}
        scatterSeed={`necro|${player?.name || (isMe ? "me" : "opp")}`}
        dread={Math.min(1, necropolis.length / 12)}
        onClick={onNecropolis ? (ev) => onNecropolis(ev) : undefined} />

      {/* ── Draw piles ── */}
      {/* ⚠ PARITY BUG (caP'n, July 24): the click was gated only on `isMe`,
          so the grimoire opened its Draw/Search modal on ANY turn, ANY number
          of times — the 2D GRIM tile gates on canDraw (= myTurn && not yet
          drawn) and this pile must obey the identical law. The glow already
          read canDraw; now the CLICK does too. Lux was called safe on the
          grounds that its handler self-gates — see the correction below. */}
      {/* ⚠ C18 AUDIT WALK (July 25): the LUX pile was the second break, and
          "the handler self-gates" was the wrong test. The 2D tiles are the
          law, and BOTH of them carry the SAME effective gate:
            Shatterfield.js:3003  LUX  enabled={isMe && myTurn && canDraw && !drawnThisTurn}
                                       onClick={isMe && myTurn ? onDrawLuxmoire : undefined}
            Shatterfield.js:3062  GRIM enabled={isMe && canDraw && !drawnThisTurn}
                                       onClick={isMe && myTurn ? onDrawGrimoire : undefined}
          FramedDrawTile (2552) fires onClick ONLY when `enabled`, and
          canDraw === myTurn && !drawnThisTurn (9208), so both reduce to
          exactly `isMe && canDraw`. The 3D lux read `isMe` alone: on the
          adversary's turn the handler's own `if (!myTurn) return` swallowed
          it — invisible, but a permission the 2D board does not grant — and
          AFTER the draw was spent the handler did NOT return: the radial
          opened with both actions greyed, on a tile 2D renders dead. The
          renderer must PERMIT nothing 2D forbids, not merely fail safely.
          Identical gate now, and the glow carries the 2D `enabled` state.
          The event is forwarded so the radial opens AT the pile — the
          arrow was discarding it and handleLuxmoireAction (3959) was
          falling back to screen centre. */}
      <Pile position={SEAT.grimoire} count={pileCount(player?.grimoire)} label="GRIMOIRE"
        glow={isMe && canDraw && myTurn ? (grimTone || true) : false}
        pulse={isMe ? grimPulse : null}
        onClick={isMe && canDraw && onDrawGrimoire ? () => onDrawGrimoire() : undefined} />
      <Pile position={SEAT.luxmoire} count={pileCount(player?.luxmoire)} label="LUXMOIRE"
        glow={isMe && canDraw ? (luxTone || true) : false}
        pulse={isMe ? luxPulse : null}
        onClick={isMe && canDraw && onDrawLuxmoire ? (ev) => onDrawLuxmoire(ev) : undefined} />

      {/* ── Seat placard ──────────────────────────────────────────────
          SHIP 6: this used to be the ONLY HUD in 3D, so it carried the
          name, the Anima, the Khao and the hand count in 11px text.
          <FieldChrome3D> now carries all four in the real duel chrome —
          the heart, the vortex, the ornate plate — so the in-world copy
          would be a second source of truth for the same numbers, in a
          worse font. It keeps ONE job: telling you whose end of the
          table you're looking at while the camera orbits. */}
      <Html position={SEAT.plate} zIndexRange={[10, 4]} style={{ pointerEvents: "none" }}>
        <div style={{
          background: "rgba(7,4,16,0.72)",
          border: `1px solid ${isMe ? GM.inkGold + "44" : "#a8323255"}`,
          borderRadius: 4, padding: "3px 11px", whiteSpace: "nowrap",
          fontFamily: GM.font, fontSize: 10, letterSpacing: 2.5,
          textTransform: "uppercase", fontStyle: "italic", fontWeight: 900,
          color: isMe ? GM.inkGoldBright : "#e05555",
          textShadow: `0 0 8px ${isMe ? GM.inkGold : "#c03a3a"}55`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
          opacity: 0.85,
        }}>
          {player?.name || (isMe ? "You" : "Adversary")}
        </div>
      </Html>
    </group>
  );
}

// ── My hand — fanned, tilted at the camera, hover/select raise ──
// ⚠ TILT LAW (the 4.2 bug): HAND_TILT rotates about +X, which lifts the
// card's FAR edge and points its face up-and-toward the camera — a hand
// held in front of you. It was negative, which leaned every card AWAY
// from the seat: faces angled at the ceiling, cards read squashed.
// The fan splay is a YAW (about the card's own face normal), NOT a roll
// on Z. Euler XYZ = Rx·Ry, so the yaw happens in the card's plane first
// and the whole fan then tilts as one sheet. A Z roll instead lifted one
// corner of every card off that sheet — the "wrong way" tilt on screen.
const HAND_TILT = 0.55;
// Spread is count-aware: a 3-card hand should sit in a tight fan, not
// three lonely cards strung across the table edge. Cards step ~HAND_GAP
// apart until the fan hits its full arc, then they overlap like a real
// hand. (Revert to the old fixed arc by hard-coding `spreadK = 1`.)
const HAND_GAP = 0.62;   // world units between neighbouring card centers
const FAN_ARC = 0.52;    // full-arc half-angle, reached at ~6 cards
// ── THE HAND THAT LAYERS (July 26) ─────────────────────────────────
// caP'n: "there's 2 cards I can't see when the game starts, 2 and 4 are
// hidden behind the other cards."
//
// TWO ROOT CAUSES, and the second one is the real culprit.
//
// (1) THE DEPTH CURVE WASN'T MONOTONIC. The old z was
//        handZ + (1 − cos(ang)) · 0.6  +  i · 0.004
//     The first term is a symmetric V — biggest at BOTH ends of the
//     fan, zero at the middle — and at 0.6 it dwarfed the 0.004
//     per-card stagger meant to keep later cards in front. So the LEFT
//     half of the fan layered BACKWARDS: card 0 sat nearer the camera
//     than card 1, which sat nearer than card 2. Every card on that
//     side got its leading edge eaten by the neighbour that should
//     have been behind it.
//
// (2) NEIGHBOURS WERE EFFECTIVELY COPLANAR. Worked through for a
//     five-card opening hand, cards 1 and 2 came out 0.000032 world
//     units apart in z — and cards 0 and 3 about 0.00006 apart. That
//     is inside depth-buffer noise at this scale, so which card won
//     was down to float rounding and camera angle. A card that loses
//     that coin-flip doesn't dim or clip; it VANISHES. That is why the
//     symptom was "hidden", not "cramped", and why it hit specific
//     indices rather than the whole fan.
//
// THE FIX, same law as July 26's 2D rail: the hand MEASURES itself
// instead of trusting a constant.
//   • The span is solved from the real card count so neighbours always
//     sit a full HAND_GAP apart — and it may now open PAST the old
//     2.78 ceiling rather than packing tighter. At HAND_LIMIT (8) the
//     old fan crushed eight cards into 2.78 units — 0.397 between
//     centres on a 1.0-wide card, six of the eight reduced to slivers.
//   • ang is derived with asin() so the requested span is what you
//     actually get, instead of being approximated then clipped.
//   • The bow is cut 0.6 → 0.35 and the layer step raised 0.004 →
//     0.05, so the monotonic term DOMINATES by an order of magnitude
//     at every index. Card i+1 is now always in front of card i, by a
//     margin ~1000× the old z-fight window.
// The fan still bows and still splays; it simply stacks like a hand
// somebody is actually holding.
const HAND_SPAN_MAX = 4.4;  // widest the fan may ever open (table is 13.2)
const HAND_R        = 5.4;  // the arc's radius
const HAND_BOW      = 0.35; // how far the arc's ends lean toward the player
const HAND_LAYER    = 0.05; // monotonic depth step — MUST dominate the bow
function fanPose(i, n) {
  const t = n === 1 ? 0 : i / (n - 1) - 0.5;
  // Full separation whenever it fits; only close ranks when it can't.
  const span = Math.min(HAND_GAP * Math.max(n - 1, 1), HAND_SPAN_MAX);
  // Solve the half-angle that actually yields `span` across the arc,
  // capped by FAN_ARC's own geometry so the fan can't invert.
  const angMax = Math.min(FAN_ARC, Math.asin(Math.min(0.9, span / (2 * HAND_R))));
  const ang = t * 2 * angMax;
  return {
    x: Math.sin(ang) * HAND_R,
    y: SEAT.handY + Math.cos(ang) * 0.1,
    z: SEAT.handZ + (1 - Math.cos(ang)) * HAND_BOW + i * HAND_LAYER,
    yaw: -ang * 0.5,   // outward splay: leftmost card's top leans left
  };
}

function HandCard({ cardId, i, n, selected, onClick, tier = null }) {
  const ref = useRef();
  const [hover, setHover] = useState(false);
  const pose = fanPose(i, n);
  const lift = selected ? 0.5 : hover ? 0.28 : 0;
  const reportHover = useContext(HoverReportCtx);
  const hoverToken = useRef(Math.random().toString(36).slice(2)).current;

  useFrame(() => {
    if (!ref.current) return;
    // Imperative lerp toward the lifted pose — tactile, zero re-renders.
    ref.current.position.x += (pose.x - ref.current.position.x) * 0.18;
    ref.current.position.y += (pose.y + lift - ref.current.position.y) * 0.18;
    ref.current.position.z += (pose.z - lift * 0.35 - ref.current.position.z) * 0.18;
  });

  // A card leaving the hand (played) while hovered never fires pointerOut.
  useEffect(() => () => { if (reportHover) reportHover({ token: hoverToken, clear: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <group ref={ref} position={[pose.x, pose.y, pose.z]}
           rotation={[HAND_TILT, pose.yaw, 0]}>
      <Card3D
        cardId={cardId}
        position={[0, 0, 0]}
        glow={selected}
        tier={tier}
        onClick={(ev) => onClick(i, ev)}
      />
      {/* invisible hover pad slightly larger than the card */}
      <mesh
        onPointerOver={(e) => {
          cursor.over(e); setHover(true);
          if (reportHover) reportHover({ token: hoverToken, cardId, faceDown: false });
        }}
        onPointerOut={(e) => {
          cursor.out(e); setHover(false);
          if (reportHover) reportHover({ token: hoverToken, clear: true });
        }}
        onClick={(e) => { e.stopPropagation(); onClick(i, evShim(e)); }}
      >
        <boxGeometry args={[CARD.W * 1.02, CARD.T * 3, CARD.H * 1.02]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

function MyHand({ hand, selectedHandIdx, onHandCardClick, suppressed = {},
                  holoOf = null }) {
  const tOf = (id) => {
    if (!holoOf || !id) return null;
    try { return holoOf(id) || null; } catch (_) { return null; }
  };
  return (
    <group>
      {hand.map((cardId, i) => (
        suppressed[`m:hand:${i}`] ? null : (
          <HandCard key={`${cardId}|${i}`} cardId={cardId} i={i} n={hand.length}
            tier={tOf(cardId)}
            selected={selectedHandIdx === i} onClick={onHandCardClick} />
        )
      ))}
    </group>
  );
}

// Opponent's hand — face-down backs, count made physical. Hidden info
// stays hidden: this renders BACKS regardless of what the data holds.
// airborneTail = draws currently in flight; hide that many from the end.
function OppHand({ count, airborneTail = 0 }) {
  const shown = Math.max(0, Math.min(count, 12) - airborneTail);
  return (
    <group>
      {Array.from({ length: shown }).map((_, i) => {
        const t = count === 1 ? 0 : i / (count - 1) - 0.5;
        return (
          <Card3D key={i} faceDown
            position={[t * Math.min(count * 0.55, 3.6), Y.card + CARD.T / 2 + i * 0.004, SEAT.handZ - 0.6]}
            rotation={[0, -t * 0.12, 0]} />
        );
      })}
    </group>
  );
}

// ── THE COMPANION — your buddy, standing on the felt ──────────────
// SHIP 7.5. In 2D the companion lives inside <PlayerSide>, which the 3D
// branch never renders — so it vanished the moment the field went 3D.
// It's back, and it's IN the duel now: the same component, mounted in a
// plain billboard <Html> (drei's transform mode stays banned; billboard
// mode is proven) anchored to a world spot on the table. It paces, it
// bobs, it throws hearts when you land a hit and eats a POW! when you
// take one — all of that is the 2D component doing what it always did.
//
// What it could NEVER do in 2D: the felt under its feet answers. A gold
// flare when we strike, a crimson one when we're struck, and a ring
// turning under it the whole time. Derived from the same two Anima
// numbers the hearts are derived from — one source, two surfaces.
// Imperative on refs, zero state, zero new data.
const COMPANION_SPOT = [-3.45, 0, 3.62];   // DIAL — my seat, left of the fan

function Companion3D({ myAnima = 30, oppAnima = 30 }) {
  const pool = useRef();
  const ring = useRef();
  const prev = useRef({ my: myAnima, opp: oppAnima });
  const hit = useRef({ at: 0, warm: true });

  // Pre-built colours: `Color.set("#hex")` re-parses a string on every
  // call, and this runs in a frame loop. Three objects, allocated once.
  const cGold = useMemo(() => new THREE.Color(SD.gold), []);
  const cFlare = useMemo(() => new THREE.Color(SD.goldBright), []);
  const cBlood = useMemo(() => new THREE.Color(SD.red), []);

  useEffect(() => {
    const p = prev.current;
    // Their Anima fell → we struck. Ours fell → we were struck. If both
    // moved on one tick, the blow we TOOK wins — that's the one you feel.
    if (myAnima < p.my) hit.current = { at: Date.now(), warm: false };
    else if (oppAnima < p.opp) hit.current = { at: Date.now(), warm: true };
    prev.current = { my: myAnima, opp: oppAnima };
  }, [myAnima, oppAnima]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const k = Math.max(0, 1 - (Date.now() - hit.current.at) / 900);
    const flare = k * k;                       // quadratic decay — a hit
    if (pool.current) {
      const m = pool.current.material;
      m.opacity = 0.13 + Math.sin(t * 1.6) * 0.03 + flare * 0.48;
      if (flare > 0.02) m.color.lerpColors(cGold, hit.current.warm ? cFlare : cBlood, flare);
      else m.color.copy(cGold);
      const s = 1 + flare * 0.32;
      pool.current.scale.set(s, s, 1);
    }
    if (ring.current) {
      ring.current.rotation.z = t * 0.32;
      ring.current.material.opacity = 0.10 + flare * 0.42;
      const s = 1 + flare * 0.5;
      ring.current.scale.set(s, s, 1);
    }
  });

  return (
    <group position={COMPANION_SPOT}>
      {/* the felt answering — a pool it stands in, and a ring turning */}
      <mesh ref={pool} rotation={[-Math.PI / 2, 0, 0]} position={[0, Y.seam + 0.003, 0]}>
        <circleGeometry args={[0.46, 30]} />
        <meshBasicMaterial color={SD.gold} transparent opacity={0.13}
          depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, Y.seam + 0.005, 0]}>
        <ringGeometry args={[0.50, 0.55, 40, 1, 0, Math.PI * 1.55]} />
        <meshBasicMaterial color={SD.goldBright} transparent opacity={0.1}
          side={THREE.DoubleSide} depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
      {/* THE ONE COMPANION — the 2D component, in the world */}
      <Html position={[0, 0.42, 0]} center distanceFactor={7.2}
        zIndexRange={[10, 4]} style={{ pointerEvents: "none" }}>
        <CompanionHUD myAnima={myAnima} oppAnima={oppAnima} size={92} />
      </Html>
    </group>
  );
}

// ── The table + the seam sigil ──
// SHIP 7 gives it a BODY. Until now the plate was a zero-thickness
// plane: from the duel camera (y 2.6, straight down the seam) the whole
// Shatterfield was a sheet of paper. It's an obsidian slab in a forged
// gold frame now — 0.2 units deep, so the low shots have something to
// stand on and the rim catches the light. Y-elevation law honoured: the
// gold lip sits at −0.008, strictly under the felt at 0.
function FieldTable({ myTurn = false }) {
  const sigilRef = useRef();
  const haloRef = useRef();
  const rimRef = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (sigilRef.current) {
      sigilRef.current.rotation.y = t * 0.4;
      sigilRef.current.position.y = 0.14 + Math.sin(t * 1.2) * 0.02;
      // SHIP 2 — the sigil is the turn beacon.
      const m = sigilRef.current.material;
      const pulse = myTurn ? 1.15 + Math.sin(t * 2.2) * 0.35 : 0.35;
      m.emissiveIntensity += (pulse - m.emissiveIntensity) * 0.08;
    }
    if (haloRef.current) {
      haloRef.current.rotation.z = -t * 0.22;
      haloRef.current.material.opacity = 0.28 + Math.sin(t * 1.4) * 0.10;
    }
    if (rimRef.current) {
      // Light runs the frame — brighter on your turn, always breathing.
      const m = rimRef.current.material;
      const goal = (myTurn ? 0.65 : 0.30) + Math.sin(t * 0.8) * 0.10;
      m.emissiveIntensity += (goal - m.emissiveIntensity) * 0.05;
    }
  });
  return (
    <group>
      {/* The forged gold frame — a lip all the way around the slab */}
      <mesh ref={rimRef} position={[0, -0.108, 0]} receiveShadow>
        <boxGeometry args={[TABLE.W + 0.44, 0.20, TABLE.D + 0.44]} />
        <meshStandardMaterial color={SD.gold} emissive={SD.gold}
          emissiveIntensity={0.4} metalness={0.85} roughness={0.34} />
      </mesh>
      {/* The obsidian body — what the duel is actually standing on */}
      <mesh position={[0, -0.145, 0]}>
        <boxGeometry args={[TABLE.W + 0.02, 0.26, TABLE.D + 0.02]} />
        <meshStandardMaterial color="#05030f" metalness={0.35} roughness={0.55} />
      </mesh>
      {/* The felt — unchanged texture, now with a whisper of clearcoat
          so the overhead lamp actually lands on it. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[TABLE.W, TABLE.D]} />
        <meshPhysicalMaterial map={feltTexture()} roughness={0.86} metalness={0.10}
          clearcoat={0.5} clearcoatRoughness={0.42} />
      </mesh>
      {/* Center sigil — the four-point diamond, now a body in the world */}
      <mesh ref={sigilRef} position={[0, 0.14, 0]} scale={[0.16, 0.24, 0.16]}>
        <octahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color={SD.gold} emissive={SD.gold}
          emissiveIntensity={0.9} toneMapped={false} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* …and a halo turning the other way beneath it */}
      <mesh ref={haloRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, Y.seam + 0.006, 0]}>
        <ringGeometry args={[0.34, 0.40, 56]} />
        <meshBasicMaterial color={SD.goldBright} transparent opacity={0.3}
          side={THREE.DoubleSide} depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── Camera rig — goal-based, the user always wins (verbatim pattern) ──
// SHIP 2: keyed on {mode, n} so pressing the SAME preset re-flies home
// after a free orbit — the button always answers.
function CameraRig({ cam }) {
  const controls = useRef();
  const { camera } = useThree();
  const goal = useRef({
    pos: new THREE.Vector3(...CAM.table.pos),
    tgt: new THREE.Vector3(...CAM.table.tgt),
    active: true,
    lastKey: null,
  });

  useFrame(() => {
    // Publish live camera distance for the stat-overlay zoom gate (4.2).
    // Distance to the field center (0,0,0) — the same anchor the presets
    // aim near. One write per frame, read by every gem's fade lerp.
    CAM_STATE.dist = camera.position.length();

    const g = goal.current;
    const key = `${cam.mode}|${cam.n}`;
    if (g.lastKey !== key) {
      const preset = CAM[cam.mode] || CAM.table;
      g.pos.set(...preset.pos);
      g.tgt.set(...preset.tgt);
      g.active = true;
      g.lastKey = key;
    }
    if (g.active && controls.current) {
      camera.position.lerp(g.pos, 0.055);
      controls.current.target.lerp(g.tgt, 0.075);
      controls.current.update();
      if (camera.position.distanceTo(g.pos) < 0.15) g.active = false;
    }
  });

  return (
    <OrbitControls ref={controls} enableDamping dampingFactor={0.08}
      minDistance={3} maxDistance={26} maxPolarAngle={Math.PI / 2.06}
      onStart={() => { goal.current.active = false; }} />
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN — the drop-in field renderer
// ═══════════════════════════════════════════════════════════════════
export default memo(function Shatterfield3D({
  me, opponent, myTurn = false,
  attackingSlot = null, targetMode = null,
  selectedHandIdx = null, canDraw = false,
  onMySlot, onOppSlot, onMyTome, onOppTome,
  onMyNecropolis, onOppNecropolis,
  // SHIP 8.4 — the Exile pit's door. Optional and degrades: absent, the
  // pit is view-only on both seats, exactly like the adversary's 2D tile.
  onMyExile = null,
  onHandCardClick, onDrawGrimoire, onDrawLuxmoire,
  // ── THE DRAW PULSE. One object in: { lux, grim, sealed }. The tones
  // are resolved upstream from the player's equipped cosmetic, so this
  // renderer never reads Firebase and never learns what a cosmetic is.
  drawCue = null,
  effLyt = null, effLyf = null,
  onCardHover = null,
  holoOf = null,
  // ── H22c — PRIMERAS, THE STAMPS. Optional and degrades: without it
  // the beings simply wear no seals (which is exactly what every
  // surface did before H22 opted in). The renderer never decides
  // whether a stamp is legal — Shatterfield.js hands down the same
  // pre-judged object its own field cards get, and the press returns
  // straight into `handlePressPrimera`, one engine for both boards.
  stampsFor = null,
  // ⬡ SHIP 6 — chrome input. OPTIONAL and degrades gracefully: no
  // sharedTimer → the framed timers simply don't mount. The board never
  // breaks for want of chrome. (`drawnThisTurn` used to ride along here
  // for the LUX/GRIM HUD tiles; those are deleted — the world's own
  // Grimoire and Luxmoire piles are the one draw surface now. Callers
  // may still pass it; it is ignored rather than a break.)
  sharedTimer = null,
}) {
  const [cam, setCam] = useState({ mode: "table", n: 0 });
  const hand = me?.hand || [];
  // Which rifts should light up for the card in my hand right now?
  const dropHint = selectedHandIdx != null ? dropHintFor(hand[selectedHandIdx]) : null;

  // ⬡ CSS3D API — one renderer + one scene for every DOM card face.
  // Built once the overlay div exists; torn down with the board.
  const cssHostRef = useRef(null);
  useEffect(() => {
    const host = cssHostRef.current;
    if (!host) return undefined;
    const renderer = new CSS3DRenderer({ element: host });
    renderer.setSize(host.clientWidth || 1, host.clientHeight || 1);
    const scene = new THREE.Scene();
    setCssApi({ renderer, scene, w: 0, h: 0 });
    return () => { setCssApi(null); host.innerHTML = ""; };
  }, []);

  // ⬡ SHIP 5 — the foil resolver lives in a REF for the transition
  // detector below. If it were in that effect's dep list, a new
  // collection identity from Firebase would re-run the diff and launch
  // phantom flights. The detector answers to [me, opponent] ONLY.
  const holoRef = useRef(holoOf);
  holoRef.current = holoOf;
  const flightTier = (cardId, isMe) => {
    if (!isMe || !holoRef.current || !cardId) return null;
    try { return holoRef.current(cardId) || null; } catch (_) { return null; }
  };

  // ═══ SHIP 3 — MOTION STATE ═══
  const [flights, setFlights] = useState([]);
  const [suppressed, setSuppressed] = useState({});
  const [fx, setFx] = useState({ shakes: {}, pulses: {}, floats: [], strikes: {}, impacts: {} });
  // H21 — the dial, read once at this level and handed DOWN. Reading
  // it per-card would put ~20 subscribers on one store for a value
  // that changes when a human taps a button; one subscriber and a ref
  // is the honest cost. The refs keep the diff effect's closure fresh
  // without putting the tier in its dep list (which would re-run the
  // detector and launch phantom flights — the same trap holoRef
  // documents above).
  const motionTier = useMotion();
  const tierRef = useRef(motionTier); tierRef.current = motionTier;
  const rateRef = useRef(1); rateRef.current = motionRate(motionTier);
  const motionLive = motionOn(motionTier);
  const liveRef = useRef(motionLive); liveRef.current = motionLive;
  // SHIP 6 — one-shot world effects. Both self-retire: the component
  // calls onDone when its own timeline finishes and drops out of state.
  const [rings, setRings] = useState([]);
  const [bursts, setBursts] = useState([]);
  // H21 — the school marks. Same one-shot discipline as rings/bursts.
  const [sigils, setSigils] = useState([]);
  // SHIP 8.2 — the ghosts. A body's last moment, world-space, one-shot.
  const [ghosts, setGhosts] = useState([]);
  const dropGhost = useCallback((id) => setGhosts((g) => g.filter((x) => x.id !== id)), []);
  // SHIP 8.5 — the death marks: the Necropolis answering a ruin.
  const [marks, setMarks] = useState([]);
  const dropMark = useCallback((id) => setMarks((m) => m.filter((x) => x.id !== id)), []);
  // A mark is scheduled to the ghost's arrival, so its timer must die
  // with the board — same discipline as dropRaf: no setState held by a
  // timer on a dead component.
  const markTimers = useRef([]);
  useEffect(() => () => { markTimers.current.forEach(clearTimeout); markTimers.current = []; }, []);
  // The board's own mount is not an event a body should announce. Stamped
  // in a state INITIALISER — i.e. during this component's first render,
  // BEFORE any child body's layout effect can read it. An effect here
  // would run AFTER the children's (React commits child effects first),
  // and every being on a freshly opened board would materialise at once.
  useState(() => { beingLife.boardMounted(); return 0; });
  // ═══ H23 — THE CUE POOL ═══
  // Every live cue from every score currently playing, flattened into
  // one list. Flat on purpose: a "score" is an authoring convenience,
  // not a runtime object — the renderer only ever has cues, each of
  // which retires itself, so a chord can outlive the card that started
  // it (an Ascend mid-Pounce) without leaking or double-firing.
  const [cues, setCues] = useState([]);
  const prevRef = useRef(null);
  const flightIdRef = useRef(0);
  const scoreIdRef = useRef(0);
  const turnKeyRef = useRef("mine");
  turnKeyRef.current = myTurn ? "mine" : "theirs";

  // ── FIRE A SCORE ─────────────────────────────────────────────────
  // The ONE entry point. Resolves the score's anchors to world space,
  // mounts its cues, and buys the turn clock back for exactly as long
  // as the chord runs. Returns the ms held, or 0 if nothing played.
  //
  // ⚠ THE HOLD IS PART OF THE FIRE, NOT A SEPARATE CALL. Every path
  // that plays a chord must also pay the clock for it, and the only
  // way to guarantee that is to make it impossible to do one without
  // the other. (The cap and the PvP reasoning live in strikeLanguage.)
  const fireScore = useCallback((score, ctx, turnKey) => {
    if (!score || !liveRef.current) return 0;
    const resolved = resolveScore(score, { ...ctx, id: `sc${++scoreIdRef.current}` });
    if (!resolved.length) return 0;
    setCues((c) => [...c, ...resolved].slice(-CUE_CAP));
    // ── H24 — THE WORLD KICK ─────────────────────────────────────
    // A score may declare `shake` and the world answers. Same bus the
    // Dragon's Maul and the Anima thunder have used since Ship 6, so
    // there is exactly one shake in flight at any moment and the last
    // one to fire wins — which is correct: two simultaneous kicks
    // should not add up into something that looks like a bug.
    //
    // `at` lets the kick land on the beat rather than at the top of
    // the score — Show Stomper's foot comes down a full second in, and
    // a table that jumped when the pillar STARTED would be shaking at
    // nothing. setTimeout rather than a frame hook because this writes
    // one plain number to a module object; there is no per-frame work
    // to schedule and no state to leak.
    if (score.shake && liveRef.current) {
      const sk = score.shake;
      const span = scaleMs(sk.ms || 420, tierRef.current);
      const kick = () => {
        FX_BUS.shakeSpan = span;
        FX_BUS.shakeUntil = Date.now() + span;
        FX_BUS.shakeMag = Math.min(0.17, sk.mag || 0.08);
      };
      const delay = scaleMs(sk.at || 0, tierRef.current);
      if (delay > 16) setTimeout(kick, delay); else kick();
    }
    // ── H24 — THE SURGE ──────────────────────────────────────────
    // The atmosphere takes the score's colour. Same one-writer,
    // timestamp-and-decay shape as the shake and as ATMOS.strikeAt —
    // `surge()` writes four numbers onto a module object and the
    // readers in FieldAtmos3D decay off them. No state, no
    // subscription, and the atmosphere still reconciles nothing on a
    // Firebase tick, which is the whole reason that ship is built the
    // way it is.
    if (score.surge && liveRef.current) {
      const sg = score.surge;
      const fire = () => surge(sg.hue, sg.mag, scaleMs(sg.ms || 900, tierRef.current));
      const d2 = scaleMs(sg.at || 0, tierRef.current);
      if (d2 > 16) setTimeout(fire, d2); else fire();
    }
    return holdFor(scoreHoldMs(score), turnKey);
  }, []);
  // ── H24 — RETIREMENT, BATCHED ────────────────────────────────────
  // Every cue calls onDone on the exact frame it finishes, and the
  // first cut of this turned each of those into its own setState. A
  // chord is 3–13 cues and they mostly end within a few frames of each
  // other, so a single U'hlti resolving used to schedule up to thirteen
  // separate state updates — each one re-rendering the whole cue layer
  // and rebuilding its children list.
  //
  // Worse, these fire from inside useFrame. React 18 does auto-batch
  // there, but auto-batching only collapses updates within one tick —
  // cues retiring on consecutive frames still cost a render apiece, and
  // the end of a big chord is exactly when the board is already busy.
  // That is the shape of a hitch you cannot find by inspection, which
  // is precisely what ie-realtime-perf warns takes a whole session to
  // track down.
  //
  // So: ids land in a ref (free, no render) and ONE rAF flushes them
  // together. The visual result is identical — a cue is invisible on
  // the frame it reports done, so unmounting it a frame later costs
  // nothing anyone can see — and the render count for a 13-cue chord
  // goes from 13 to 1.
  const pendingDrop = useRef([]);
  const dropRaf = useRef(0);
  const dropCue = useCallback((id) => {
    pendingDrop.current.push(id);
    if (dropRaf.current) return;
    dropRaf.current = requestAnimationFrame(() => {
      dropRaf.current = 0;
      const gone = pendingDrop.current;
      pendingDrop.current = [];
      if (!gone.length) return;
      setCues((c) => (c.length ? c.filter((x) => gone.indexOf(x.id) === -1) : c));
    });
  }, []);
  // A board that unmounts mid-chord must not leave a frame callback
  // holding a setState on a dead component.
  useEffect(() => () => {
    if (dropRaf.current) cancelAnimationFrame(dropRaf.current);
    dropRaf.current = 0;
    pendingDrop.current = [];
  }, []);

  // THE TURN SWEEP — a band of light crosses the felt whenever the
  // chair changes. Written to the bus, never to state.
  //
  // H24 — and the score rides alongside it. This is the single
  // most-seen animation in Terror (twice a Cycle, every Cycle, for the
  // life of the game), so it is 460ms of exactly one thing and will
  // never be allowed to grow. The `mountedRef` guard is why it does not
  // fire on the frame the board mounts: a sweep on arrival reads as a
  // turn you did not take, and toggling 2D↔3D mid-match would otherwise
  // hand you one every time.
  const sweptRef = useRef(false);
  useEffect(() => {
    FX_BUS.sweepAt = Date.now();
    if (!sweptRef.current) { sweptRef.current = true; return; }
    fireScore(boardScore("turn"),
      { self: plateWorld(true), target: plateWorld(false) }, myTurn ? "mine" : "theirs");
  }, [myTurn, fireScore]);

  // ── H23 — THE DOOR ───────────────────────────────────────────────
  // caP'n: "the animation should play once you use the actual effect."
  // The detector sees state CHANGES, which covers summons, swings,
  // returns and attachments — but an effect RESOLVING is not always a
  // state change the renderer can name (a Tome that draws and a Tome
  // that grants Anima both look like "a number moved" from out here).
  //
  // So any handler in Shatterfield.js may call fireMotion({ cardId,
  // channel, seat, slot }) at the exact instant it resolves, and this
  // board plays it. Pure presentation in, nothing out: fire into a
  // board running in 2D and there is simply no listener. Ship 1 builds
  // the door and wires the four detector-visible channels through it;
  // the Tome and Impera activation scores walk through it next.
  useEffect(() => subscribeMotionFire((evt) => {
    const card = getCard(evt.cardId);
    if (!card) return;
    const score = scoreFor(card, evt.channel || CHANNELS.ATTACH);
    if (!score) return;
    const mine = evt.seat !== "o";
    const self = Number.isInteger(evt.slot)
      ? escWorld(mine, Math.max(0, Math.min(ESCLING_SLOTS - 1, evt.slot)))
      : plateWorld(mine);
    fireScore(score, { self, target: plateWorld(!mine) }, turnKeyRef.current);
  }), [fireScore]);

  useEffect(() => { ensureSf3dKeyframes(); }, []);

  // The transition detector: diff prev props vs current props and make
  // every change PHYSICAL. First run only snapshots (no spurious flights
  // on mount or on 2D↔3D toggle).
  useEffect(() => {
    const snapSeat = (p) => ({
      hand: [...(p?.hand || [])],
      // Replay capture — same law as the live board: a literal here would
      // record a four-rift battle line as three and the replay would be
      // missing a being that was on the field.
      esc: riftArray(p?.field?.esclings, ESCLING_SLOTS).map((s) =>
        s ? { escendant: s.escendant || null, currentLyf: s.currentLyf ?? null,
              // H21a — the swing. `attackedThisTurn` is a Firebase
              // field, so this diff fires on BOTH clients off the same
              // state: no bus, no extra writes, and the adversary sees
              // the Maul too.
              attacked: !!s.attackedThisTurn } : null),
      tomes: riftArray(p?.field?.tomes, 3).map((t) => tomeInfo(t)),
      // H21 — three channels Ship 3 never diffed, because before the
      // Strike Language there was nothing distinct to SAY about them.
      curios: riftArray(p?.field?.curios, 2).map((c) =>
        (typeof c === "object" && c !== null ? c?.id : c) || null),
      prophecy: (p?.field?.prophecy || p?.prophecy || null),
      // The Soul is the attachment ladder's home: Impera → U'hlti →
      // Affinity all land HERE, never in a rift. Flattened per slot so
      // a multiset diff finds exactly what was just attached.
      // Souls are read per Escling rift, so this must span the same width as
      // the row above — a literal 3 would silently drop the last rift's soul.
      souls: riftArray(p?.field?.esclings, ESCLING_SLOTS).map((sl) =>
        [...((sl && sl.soul) || [])]),
      necro: [...(Array.isArray(p?.necropolis) ? p.necropolis : [])],
      grim: pileCount(p?.grimoire),
      lux: pileCount(p?.luxmoire),
      anima: p?.anima ?? 30,
    });
    const snap = { m: snapSeat(me), o: snapSeat(opponent) };
    const prev = prevRef.current;
    prevRef.current = snap;
    if (!prev) return;
    // ── T17 — THE DIAL, at OFF ───────────────────────────────────
    // OFF means the effects never MOUNT, not "play instantly". The
    // snapshot above still advances, so flipping the dial back to FULL
    // mid-duel resumes cleanly instead of firing a backlog of flights
    // for everything that happened while it was off. The live stat
    // gems (DigitalStat) never stop reading the truth, so a player at
    // OFF still sees every number the moment it changes — they just
    // don't watch it travel.
    if (!liveRef.current) return;

    const newFlights = [];
    const supprAdd = {};
    const shakes = {}; const pulses = {}; const floats = []; const shards = [];
    // H21 — two new buckets. `strikes` carries the swing (profile +
    // the timestamp that restarts it); `impacts` carries the victim's
    // own wound numbers so Card3D's shake reads the right amplitude.
    const strikes = {}; const impacts = {};
    // SHIP 8.2 — ghosts spawned this tick (world-space, one per event).
    const newGhosts = [];
    const newMarks = [];
    const ghostYaw = (isMe) => (isMe ? STANCE.mine : STANCE.theirs + Math.PI);
    // ── H23 — THE AIM ────────────────────────────────────────────
    // A Lyt Attack score needs to know WHERE to send its beam, and the
    // detector is the only place in the renderer that knows. It can't
    // be answered inside diffSeat because the victim is in the OTHER
    // seat, which hasn't been diffed yet when the attacker's swing is
    // found. So both seats record what they lost, both record who
    // swung, and the pairing happens after — one pass, no bus, no
    // extra Firebase field, and it resolves identically on both
    // clients because both are diffing the same two snapshots.
    //
    // Fallback when nothing was wounded: the enemy's own seat plate.
    // That is not a cop-out, it is correct — an unblocked attack IS
    // aimed at the adversary, and Spectral Paws exists precisely to
    // make that legal.
    const victims = { m: [], o: [] };
    const pendingStrikes = [];
    // The detector answers to [me, opponent] ONLY (see holoRef above),
    // so `myTurn` is read through a ref here for the same reason every
    // other cross-cutting value in this effect is: putting it in the
    // dep list would re-run the diff and launch phantom flights. The
    // key only ever gates the per-turn hold budget.
    const turnKey = turnKeyRef.current;
    const now = Date.now();
    const fid = () => `fl${now}-${++flightIdRef.current}`;

    const diffSeat = (seatKey, isMe, ps, cs) => {
      const startLen = newFlights.length;
      const removed = multisetDiff(ps.hand, cs.hand);

      // ── DRAWS — arc off the pile; mine flip face-up mid-air,
      //    theirs stay hidden the whole flight (hidden info law) ──
      if (cs.hand.length > ps.hand.length) {
        const k = cs.hand.length - ps.hand.length;
        const fromLux = cs.lux < ps.lux && cs.grim >= ps.grim;
        const fromPos = pileWorld(isMe, fromLux ? SEAT.luxmoire : SEAT.grimoire,
          fromLux ? cs.lux : cs.grim);
        for (let j = cs.hand.length - k; j < cs.hand.length; j++) {
          if (isMe) {
            newFlights.push(stampFlight({ id: fid(), cardId: cs.hand[j],
              from: fromPos, to: myHandWorld(j, cs.hand.length), flip: true,
              hidden: false, suppress: `m:hand:${j}` }, null, "draw"));
          } else {
            newFlights.push(stampFlight({ id: fid(), cardId: cs.hand[j],
              from: fromPos, to: oppHandWorld(), flip: false, hidden: true,
              tag: "oppDraw" }, null, "draw"));
          }
        }
      }

      // ── ESCENDANT SLOTS — plays, revivals, hit shakes, damage floats ──
      // ⚠ SHIP 8 (2026-09-15): this loop, the Soul loop and the Necropolis
      // origin search below all read `i < 3` — the battle line has been
      // FOUR since R110 (2026-09-06) and the snapshot above is already
      // ESCLING_SLOTS wide. The Mind rift (index 3) had NO summon flight,
      // no strike, no wound, no Ascend chord and no ruin shards on the 3D
      // board for nine days; the first standing body in that rift made it
      // obvious. Same law as the seat: ESCLING_SLOTS, never a literal.
      for (let i = 0; i < ESCLING_SLOTS; i++) {
        const pe = ps.esc[i]?.escendant || null;
        const ce = cs.esc[i]?.escendant || null;
        if (ce && ce !== pe) {
          // ── H24 — THE LADDER ─────────────────────────────────────
          // Escling → Beb → Champion → Lord. Every rung is a card
          // REPLACING another in the same rift, which from out here
          // looks identical to a fresh being being played — and until
          // now it was treated as one, so a hard-won Ascend to Lord
          // wore the same Descent as any Beb hitting the felt.
          //
          // The tell is the SOUL. A being that climbed leaves its lower
          // self in its own Soul stack ("a card's lineage — its history
          // of what it ascended from"), so the rung is detectable with
          // no new Firebase field and no bus: the slot's soul GAINED
          // exactly the card that used to be standing here.
          //
          // A being simply swapped out would not do that, which is what
          // makes this safe rather than merely clever.
          // The inference itself lives in boardMotion as a pure
          // function — it is the only real reasoning in the whole
          // animation system (everything else is a table lookup), and
          // inline here it could not be tested. DEVIANCE is decided in
          // there too: school matches, Ilk does not, permanent, and it
          // must never wear the Ascend's gold.
          const climb = climbInfo(pe, ce, ps.souls[i], cs.souls[i], getCard);
          const climbed = climb.climbed;
          if (climbed) {
            fireScore(ladderScore(climb.from, climb.to, climb.deviant),
              { self: escWorld(isMe, i), target: plateWorld(!isMe) }, turnKey);
            // SHIP 8.2 — THE ASCENT, IN THE FLESH. The lower body flashes
            // out where it stood (it did not leave — it went into the
            // Soul) while the higher card is in the air; the higher body
            // rises gold when that card lands. `mark` is what the
            // mounting body reads; the ghost is what stands in for the
            // one that just unmounted.
            beingLife.mark(`${seatKey}:esc:${i}`, "ascend");
            if (pe && beingOf(pe)) {
              const w = escWorld(isMe, i);
              newGhosts.push({ id: fid(), cardId: pe, from: w, to: null, yaw: ghostYaw(isMe), kind: "ascend" });
            }
          } else if (pe && ce && (ps.souls[i] || []).includes(ce)) {
            // SHIP 8.2 — THE DESCENT. The higher form was ruined and its
            // lower self rose out of the Soul to fill the rift (RULEZ_V2
            // ch.Ascending). No flight carries it, so the card swaps in
            // place and the body mounts on the next render — tell it
            // what it is: a survivor standing again, not a summon.
            beingLife.mark(`${seatKey}:esc:${i}`, "descend");
          }
          const ri = removed.indexOf(ce);
          if (ri !== -1) {
            removed.splice(ri, 1); // played from hand
            const hi = ps.hand.indexOf(ce);
            const from = isMe ? myHandWorld(Math.max(hi, 0), ps.hand.length || 1) : oppHandWorld();
            // H23 — THE SUMMON rides the flight and fires on TOUCHDOWN,
            // not on launch. A chord that starts while the card is
            // still in the air plays over an empty rift; one that
            // starts as it lands is the being arriving. (Unauthored
            // beings carry no score and land exactly as they did
            // before — H21's Descent is still the whole answer there.)
            // ⚠ A CLIMB IS NOT AN ARRIVAL. When the ladder fired above,
            // the summon is deliberately withheld — otherwise a single
            // Ascend plays TWO full chords over one rift, which is both
            // twice the hold and, worse, two different stories about
            // the same event. The being did not take the field; it was
            // already standing there and got better.
            newFlights.push(stampFlight({ id: fid(), cardId: ce, from,
              to: escWorld(isMe, i), flip: !isMe, hidden: false,
              score: climbed ? null : scoreFor(getCard(ce), CHANNELS.SUMMON),
              scoreCtx: { self: escWorld(isMe, i), target: plateWorld(!isMe) },
              suppress: `${seatKey}:esc:${i}` }, getCard(ce)));
          } else if (ps.necro.includes(ce) && cs.necro.length < ps.necro.length) {
            // REVIVAL — necropolis → rift, canon green glow on arrival
            newFlights.push(stampFlight({ id: fid(), cardId: ce,
              from: pileWorld(isMe, SEAT.necropolis, ps.necro.length),
              to: escWorld(isMe, i), flip: false, hidden: false,
              suppress: `${seatKey}:esc:${i}` }, getCard(ce)));
            pulses[`${seatKey}:esc:${i}`] = { color: "#4ade80", until: now + 2400 };
            // SHIP 8.2 — the body that lands on this flight rises green.
            beingLife.mark(`${seatKey}:esc:${i}`, "revive");
            // H23 — THE RITE OF RETURN fires IMMEDIATELY, unlike the
            // summon: the ground has to already be giving something
            // back before the being can arrive into it. The card flies
            // home through its own rite, which is the only ordering
            // that reads as a resurrection rather than as a landing
            // with decoration. R44/R77: the rite does not care which
            // method paid — paid, Ritual, or the Time stamp, the
            // ground opens the same way.
            fireScore(scoreFor(getCard(ce), CHANNELS.RETURN),
              { self: escWorld(isMe, i), target: plateWorld(!isMe) }, turnKey);
          }
        }
        const plyf = ps.esc[i]?.currentLyf, clyf = cs.esc[i]?.currentLyf;
        if (pe && ce === pe && plyf != null && clyf != null && clyf < plyf) {
          // H21 — THE IMPACT. The wound's shape is the VICTIM's own
          // (canon already rules the colour: "every hit carries the
          // victim's blood-hue"). A Dragon gets rocked, a Phantom
          // barely registers, a Space being takes one surgical jolt.
          const imp = impactFor(getCard(ce));
          shakes[`${seatKey}:esc:${i}`] = now + scaleMs(imp.ms, tierRef.current);
          impacts[`${seatKey}:esc:${i}`] = imp;
          const w = escWorld(isMe, i);
          // ── H24 pass 3 — THE SPECIES WOUND ───────────────────────
          // H21's shake and the damage float still do exactly what they
          // did; this sits UNDER them and is the only part that knows
          // whose body it is. 2–3 cues, ~400ms, and deliberately no
          // toll — the attack already rang, a beat ago, and ringing
          // again for the same exchange double-counts it.
          fireScore(scoreFor(getCard(ce), CHANNELS.HURT),
            { self: [w[0], w[1] + 0.24, w[2]], target: plateWorld(!isMe) }, turnKey);
          floats.push({ id: fid(), pos: [w[0], w[1] + 0.6, w[2]],
            text: `−${plyf - clyf}`, color: "#ff6b6b" });
          // H23 — this slot is a valid aim point for whoever swung.
          victims[seatKey].push([w[0], w[1] + 0.28, w[2]]);
          // ── H24 — FRACTURED ──────────────────────────────────────
          // Effective Lyf 0 and STILL STANDING. The lexicon is precise
          // and players routinely conflate the three bad states, so
          // this one gets a mark of its own: a crack under the card and
          // deliberately NO debris, because nothing died. R28 resets
          // Lyf at the owner's end of turn, so it frequently un-happens
          // — it must never read as final. 380ms, the shortest score in
          // the game, because a bloody turn produces several.
          if (clyf <= 0 && plyf > 0) {
            fireScore(boardScore("fracture"),
              { self: [w[0], w[1] + 0.2, w[2]], target: plateWorld(!isMe) }, turnKey);
          }
        }
        // ── H21a — THE STRIKE ────────────────────────────────────
        // attackedThisTurn false→true is the exact tick the being
        // swung. Until today this board had no motion for it at all.
        if (ce && ce === pe && !ps.esc[i]?.attacked && cs.esc[i]?.attacked) {
          const prof = strikeFor(getCard(ce));
          strikes[`${seatKey}:esc:${i}`] = { at: now, profile: prof, rate: rateRef.current };
          // H23 — THE RETCON. caP'n: "let's give each species its own
          // Lyt attack." The POSE stays H21's (four Ilk swings, scaled
          // by Level — that part was always right); what's new is that
          // the species now says something in the world while it
          // swings. Queued rather than fired, because the target lives
          // in the seat this pass hasn't reached yet.
          const sc = scoreFor(getCard(ce), CHANNELS.LYT);
          if (sc) {
            const w = escWorld(isMe, i);
            pendingStrikes.push({
              score: sc, seatKey, self: [w[0], w[1] + 0.28, w[2]],
              foe: seatKey === "m" ? "o" : "m", plate: plateWorld(!isMe),
            });
          }
          // Only a Lord-weight Dragon may kick the world (the registry
          // zeroes `shake` below L3). The Maul is the one strike in the
          // language heavy enough to move the table.
          if (prof.shake > 0) {
            const span = scaleMs(prof.ms, tierRef.current);
            FX_BUS.shakeSpan = span;
            FX_BUS.shakeUntil = now + span;
            FX_BUS.shakeMag = prof.shake;
          }
        }
      }

      // ── TOME placements — face-down flights never reveal ──
      for (let i = 0; i < 3; i++) {
        const pt = ps.tomes[i]?.id || null;
        const ct = cs.tomes[i] || { id: null, faceDown: false };
        if (ct.id && ct.id !== pt) {
          const ri = removed.indexOf(ct.id);
          if (ri !== -1) removed.splice(ri, 1);
          const hi = ps.hand.indexOf(ct.id);
          const from = isMe ? myHandWorld(Math.max(hi, 0), ps.hand.length || 1) : oppHandWorld();
          // ⚠ A FACE-DOWN TOME IS NOT A CASTING. It is a card being
          // SET, and the Casting's rune flare would announce spellwork
          // that hasn't happened — worse, it would leak the school
          // across the seam on a card the adversary is not allowed to
          // read. Face-down sets keep the drab Departure-weight arc;
          // the Casting fires when the tome is REVEALED.
          newFlights.push(stampFlight({ id: fid(), cardId: ct.id, from,
            to: tomeWorld(isMe, i), flip: false, hidden: ct.faceDown,
            faceDownLand: ct.faceDown,
            // ── H24 — THE CASTING ──────────────────────────────────
            // 72 Tomes, and until now not one of them said anything
            // when it actually resolved. A Nihil ward eating a U'hlti
            // and a Ritual Tome resurrecting a Lord were, visually,
            // the same nothing.
            //
            // ⚠ ONLY FACE-UP. A face-down Tome is a card being SET, not
            // cast — the rune would announce spellwork that has not
            // happened, and worse, it would leak the school's colour
            // across the seam on a card the adversary is not allowed
            // to read. The Casting waits for the reveal below.
            score: ct.faceDown ? null : scoreFor(getCard(ct.id), CHANNELS.CAST),
            scoreCtx: { self: tomeWorld(isMe, i), target: plateWorld(!isMe) },
            // SHIP 8.7 — the prop on a face-up Tome performs on touchdown
            brand: ct.faceDown ? null : { key: `${seatKey}:tome:${i}`, kind: "cast" },
            suppress: `${seatKey}:tome:${i}` },
            getCard(ct.id), ct.faceDown ? "depart" : "play"));
        } else if (ct.id && ct.id === pt && ps.tomes[i]?.faceDown && !ct.faceDown) {
          // ── THE REVEAL ─────────────────────────────────────────────
          // The set Tome flips. THIS is the moment the school is
          // legally readable and the spell actually happens, so this is
          // where the Casting fires — no flight, because the card is
          // already sitting in its rift and has been for a turn.
          fireScore(scoreFor(getCard(ct.id), CHANNELS.CAST),
            { self: tomeWorld(isMe, i), target: plateWorld(!isMe) }, turnKey);
          // SHIP 8.7 — the revealed Tome's prop performs as it is cast
          beingLife.kick(`${seatKey}:tome:${i}`, "cast");
        }
      }

      // ── CURIOS — THE OFFERING ───────────────────────────────
      // The fourth wall. A Curio's condition is checked against the
      // PLAYER, not the board, so it skims in low and flat: set down
      // by a hand rather than summoned onto the felt.
      for (let i = 0; i < 2; i++) {
        const pc = ps.curios[i] || null;
        const cc = cs.curios[i] || null;
        if (cc && cc !== pc) {
          const ri = removed.indexOf(cc);
          if (ri !== -1) removed.splice(ri, 1);
          const hi = ps.hand.indexOf(cc);
          const from = isMe ? myHandWorld(Math.max(hi, 0), ps.hand.length || 1) : oppHandWorld();
          // H24 — THE OFFERING. A Curio's condition is checked against
          // the PLAYER — what you are wearing, what is sitting at the
          // table beside you — so its score is not a casting at all.
          // The game simply ACKNOWLEDGES something that was already
          // true about you before the duel began.
          newFlights.push(stampFlight({ id: fid(), cardId: cc, from,
            to: curioWorld(isMe, i), flip: !isMe, hidden: false,
            score: scoreFor(getCard(cc), CHANNELS.OFFER),
            scoreCtx: { self: curioWorld(isMe, i), target: plateWorld(!isMe) },
            suppress: `${seatKey}:curio:${i}` }, getCard(cc)));
        }
      }

      // ── PROPHECY — THE DECLARATION ──────────────────────────
      // It does not arrive. It is DECLARED: the card holds perfectly
      // still while the light comes to it and the widest, slowest ring
      // in the language opens under it. A Prophecy is immune to every
      // card in the game (R74) and it behaves like it knows.
      if (cs.prophecy && cs.prophecy !== ps.prophecy) {
        const ri = removed.indexOf(cs.prophecy);
        if (ri !== -1) removed.splice(ri, 1);
        // H24 — and now it is actually DECLARED. The card holds
        // perfectly still (H21's `still` flight) while its own score
        // opens the slowest ring on the board around it. A Prophecy is
        // not an attack; it is a statement of intent the game has
        // agreed to honour, and nothing in it travels or breaks.
        newFlights.push(stampFlight({ id: fid(), cardId: cs.prophecy,
          from: prophWorld(isMe), to: prophWorld(isMe),
          flip: false, hidden: false,
          score: scoreFor(getCard(cs.prophecy), CHANNELS.DECLARE),
          scoreCtx: { self: prophWorld(isMe), target: plateWorld(!isMe) },
          suppress: `${seatKey}:proph` }, getCard(cs.prophecy)));
      }

      // ── THE SOUL — THE ATTACHMENT LADDER ────────────────────
      // Impera → U'hlti → Affinity. None of these ever touches a rift;
      // they attach to a being's Soul, which is why the Ship 3 detector
      // never saw them. Each lands on its OWN profile: the Impera is
      // BRANDED straight down with no bounce, the U'hlti rises slow and
      // ceremonial with the loudest flare in the game, and the Affinity
      // seals shut behind a contracting iris.
      for (let i = 0; i < ESCLING_SLOTS; i++) {
        const gained = multisetDiff(cs.souls[i] || [], ps.souls[i] || []);
        gained.forEach((sid) => {
          const sc = getCard(sid);
          // A descending being's lower level re-enters its own Soul —
          // that is a ruin unwinding, not an attachment, and it must
          // not wear the Signature. Only the ladder announces itself.
          if (!sc || (sc.type !== "Impera" && sc.type !== "U'hlti" && sc.type !== "Affinity")) return;
          const ri = removed.indexOf(sid);
          if (ri !== -1) removed.splice(ri, 1);
          const hi = ps.hand.indexOf(sid);
          const from = isMe ? myHandWorld(Math.max(hi, 0), ps.hand.length || 1) : oppHandWorld();
          // H23 — caP'n: "attaching cards can have a slightly altered
          // animation depending on the card like U'hlti vs Impera."
          // H21 already separated the FLIGHT (the Branding drops
          // straight down and does not bounce; the Signature rises
          // slow with the loudest flare in the game); this adds what
          // happens once it settles, per card. Every authored Impera
          // shares one shape — a contracting iris, because a brand
          // presses INWARD — and the U'hlti is allowed to be the
          // loudest thing on the table.
          newFlights.push(stampFlight({ id: fid(), cardId: sid, from,
            to: escWorld(isMe, i), flip: !isMe, hidden: false,
            score: scoreFor(sc, CHANNELS.ATTACH),
            scoreCtx: { self: escWorld(isMe, i), target: plateWorld(!isMe) },
            // SHIP 8.3 — the being answers the card landing on its Soul:
            // rears for a brand, throws its arms for a signature, settles
            // for a sworn seal. Fired on TOUCHDOWN (handleFlightDone).
            brand: { key: `${seatKey}:esc:${i}`,
                     kind: sc.type === "U'hlti" ? "sign" : sc.type === "Affinity" ? "seal" : "brand" } }, sc));
        });
      }

      // ── NECROPOLIS growth — descent from the slot that lost the card ──
      if (cs.necro.length > ps.necro.length) {
        cs.necro.slice(ps.necro.length).forEach((id2) => {
          let from = null, fromSlot = false;
          for (let i = 0; i < ESCLING_SLOTS; i++) {
            if (ps.esc[i]?.escendant === id2 && cs.esc[i]?.escendant !== id2) {
              from = escWorld(isMe, i); fromSlot = true; break;
            }
          }
          if (!from) {
            const ri = removed.indexOf(id2);
            if (ri !== -1) {
              removed.splice(ri, 1);
              from = isMe ? myHandWorld(0, ps.hand.length || 1) : oppHandWorld();
            }
          }
          if (from) {
            const grave = pileWorld(isMe, SEAT.necropolis, cs.necro.length);
            newFlights.push(stampFlight({ id: fid(), cardId: id2, from,
              to: grave,
              flip: false, hidden: false,
              suppress: `${seatKey}:necroTop` }, getCard(id2), "depart"));
            // SHIP 8.2 — THE BURIAL. caP'n: "let's see it flash into
            // necropolis." The body itself, as light, streaks off the
            // rift it fell on and shrinks into the pile. Only when it
            // fell FROM a rift (a hand discard has no body to bury) and
            // only for a card the kit has a body for.
            if (fromSlot && beingOf(id2)) {
              newGhosts.push({ id: fid(), cardId: id2, from, to: grave, yaw: ghostYaw(isMe), kind: "ruin" });
              // SHIP 8.5 — and the Necropolis answers: a skull of green
              // light rises out of the cellar, sized by the level that
              // just died. Delayed to the ghost's arrival, not its launch.
              newMarks.push({ id: fid(), from: grave, level: getCard(id2)?.level || 1, at: now + 620 });
            }
            // SHIP 6 — it broke where it stood. The shards fire at the
            // slot the card LEFT, not where it lands.
            shards.push({ id: fid(), pos: [from[0], from[1] + 0.06, from[2]],
              seed: `${id2}|${now}`, color: "#c8506a" });
            // ── H24 — RUINED ─────────────────────────────────────────
            // The canonical destruction pair, crack then shatter, which
            // by now the player has learned from every Omnia Tome in
            // the game. Fired at the rift the card left, alongside the
            // Ship 6 shards rather than instead of them: the shards are
            // the CARD coming apart, this is the GROUND acknowledging
            // it, and the difference between a thing breaking and a
            // board that felt it break is the entire third beat.
            fireScore(boardScore("ruin"),
              { self: [from[0], from[1] + 0.06, from[2]], target: plateWorld(!isMe) },
              turnKey);
          }
        });
      }

      // ── ANIMA deltas float at the seat plate ──
      if (cs.anima !== ps.anima) {
        const d = cs.anima - ps.anima;
        floats.push({ id: fid(), pos: plateWorld(isMe),
          text: d > 0 ? `+${d} ◆` : `−${-d} ◆`,
          color: d > 0 ? "#4ade80" : (SD.red || "#dc2626") });
        // ── H24 — THE CURRENCY ───────────────────────────────────
        // The float above is the honest, readable, unmissable answer
        // and it is not being replaced — this is the FEEL underneath
        // the number, which is why it is among the quietest scores in
        // the game. `animaScore` picks the weight: a gain rises warm, a
        // normal loss converges and rings once, and a single hit of 8
        // or more is a Bleed with the ground opening under it. Show
        // Stomper does ten in one beat; that one has earned it.
        fireScore(animaScore(d),
          { self: plateWorld(isMe), target: plateWorld(!isMe) }, turnKey);
        // SHIP 6 — THE THUNDER. A strike on either Anima kicks the
        // whole world; the magnitude rides the damage, capped so a
        // 20-point haymaker is still readable. The matching screen
        // flash is DOM and lives in <FieldChrome3D>.
        if (d < 0) {
          const span = 380 + Math.min(-d, 12) * 14;
          FX_BUS.shakeSpan = span;
          FX_BUS.shakeUntil = now + span;
          FX_BUS.shakeMag = Math.min(0.17, 0.035 + -d * 0.011);
        }
      }

      // Every flight this seat launched flies oriented to its owner —
      // and (SHIP 5) wearing its own foil. One stamp covers draws,
      // plays, tome sets, revivals and descents alike.
      for (let q = startLen; q < newFlights.length; q++) {
        newFlights[q].yaw = isMe ? 0 : Math.PI;
        newFlights[q].tier = flightTier(newFlights[q].cardId, isMe);
      }
    };

    diffSeat("m", true, prev.m, snap.m);
    diffSeat("o", false, prev.o, snap.o);

    // ── H23 — THE PAIRING ────────────────────────────────────────
    // Both seats have now reported their wounds, so every queued swing
    // can finally be told what it hit. First wounded slot in the enemy
    // seat wins the aim; if nothing over there took Lyf, the attack
    // went at the Warlock and the plate is the honest target.
    //
    // Multiple attackers in one tick (Apocalypse gives every being an
    // extra Lyt Attack) each pick the same first victim rather than
    // one apiece — deliberate. Fanning them out would be guessing at a
    // pairing the engine never recorded, and three beams converging on
    // the thing that actually died reads BETTER than three beams
    // spread across cards that may not have been touched at all.
    pendingStrikes.forEach((s) => {
      const target = victims[s.foe][0] || s.plate;
      fireScore(s.score, { self: s.self, target }, turnKey);
    });

    if (newFlights.length) {
      const capped = newFlights.slice(0, 10); // storm guard
      capped.forEach((f) => { if (f.suppress) supprAdd[f.suppress] = true; });
      setFlights((f) => [...f, ...capped]);
      if (Object.keys(supprAdd).length) setSuppressed((s) => ({ ...s, ...supprAdd }));
    }
    if (shards.length) setBursts((b) => [...b, ...shards].slice(-6));
    if (newGhosts.length) setGhosts((g) => [...g, ...newGhosts].slice(-6));
    if (newMarks.length) {
      newMarks.forEach((mk) => {
        const id = setTimeout(() => {
          markTimers.current = markTimers.current.filter((x) => x !== id);
          setMarks((m) => [...m, mk].slice(-4));
        }, Math.max(0, mk.at - Date.now()));
        markTimers.current.push(id);
      });
    }
    if (Object.keys(shakes).length || Object.keys(pulses).length
        || Object.keys(strikes).length || floats.length) {
      setFx((p2) => ({
        shakes: { ...p2.shakes, ...shakes },
        pulses: { ...p2.pulses, ...pulses },
        impacts: { ...p2.impacts, ...impacts },
        strikes: { ...p2.strikes, ...strikes },
        floats: [...p2.floats, ...floats].slice(-12),
      }));
      if (floats.length) {
        const ids = floats.map((f2) => f2.id);
        setTimeout(() => setFx((p3) => ({
          ...p3, floats: p3.floats.filter((f3) => !ids.includes(f3.id)),
        })), 1250);
      }
    }
  }, [me, opponent]);

  const handleFlightDone = (flight) => {
    setFlights((f) => f.filter((x) => x.id !== flight.id));
    if (flight.suppress) setSuppressed((s) => {
      const n = { ...s }; delete n[flight.suppress]; return n;
    });
    // SHIP 6 — a card that lands stamps the felt. Capped so a ten-card
    // storm can't stack forty rings.
    // H21 — the stamp is the profile's, not one hardcoded expansion.
    // The Affinity's ring travels INWARD (its profile sets r0 > r1) —
    // the top of the attachment ladder is denial, so the Sealing
    // closes an iris rather than radiating a shockwave.
    const rp = flight.ringProfile || {};
    setRings((r) => [...r, {
      id: `${flight.id}|ring`, pos: flight.to,
      color: rp.color || flight.ring || SD.goldBright,
      r0: rp.r0, r1: rp.r1, life: rp.life,
    }].slice(-8));
    // THE SIGIL — the school's mark blooms over the card as it lands.
    // Without it a Nihil counter and a Ritual resurrection are two
    // shades of the same flash; with it, all eight schools read at a
    // glance. Self-retiring on a timer (it is DOM, not a frame loop).
    if (flight.sigil) {
      const sid = `${flight.id}|sig`;
      setSigils((g) => [...g, {
        id: sid, glyph: flight.sigil, color: flight.sigilColor || SD.goldBright,
        pos: [flight.to[0], flight.to[1] + 0.55, flight.to[2]],
      }].slice(-6));
      setTimeout(() => setSigils((g) => g.filter((x) => x.id !== sid)), 1000);
    }
    // ── H23 — TOUCHDOWN ──────────────────────────────────────────
    // The summon and the attachment both wait for this moment. The
    // flight carried its own score here rather than the detector
    // holding one in a map, which means a chord can never fire for a
    // card whose flight was cancelled, capped by the storm guard, or
    // outlived by an Ascend mid-air. The card lands; the card speaks.
    if (flight.score) fireScore(flight.score, flight.scoreCtx, turnKeyRef.current);
    // SHIP 8.3 — the body under the landing card reacts to it.
    if (flight.brand) beingLife.kick(flight.brand.key, flight.brand.kind);
  };
  const dropRing = (id) => setRings((r) => r.filter((x) => x.id !== id));

  // H22c — THE SEAL FLARE. A stamp pressed in 3D already punches on the
  // card face (same DOM, same animation as 2D), but a press is a board
  // event and the board should say so. One ring in the word's own colour,
  // on the ring channel that already exists — no new effect system, and
  // it inherits the same 8-ring cap so a Turn of presses can't stack.
  const sealRingIdRef = useRef(0);
  const spawnSealRing = useCallback((pos, color) => {
    if (!pos) return;
    setRings((r) => [...r, {
      id: `seal-${++sealRingIdRef.current}`, pos, color: color || SD.goldBright,
    }].slice(-8));
  }, []);
  const dropBurst = (id) => setBursts((b) => b.filter((x) => x.id !== id));
  const airborneOppDraws = flights.filter((f) => f.tag === "oppDraw").length;

  // T17 — the dial's own control. Same forged-slab language as the
  // camera presets so it reads as one rail, but tinted by position:
  // FULL is gold (nothing suppressed), QUICK amber, OFF grey — the
  // colour alone tells you where the dial sits without reading it.
  const motionBtn = () => {
    const tone = motionTier === "off" ? GM.vellumFaint
      : motionTier === "quick" ? "#e8963c" : GM.inkGold;
    return (
      <button
        key="motion"
        title="Combat animation — Full / Quick / Off"
        onClick={() => {
          const i = MOTION_ORDER.indexOf(getMotion());
          setMotion(MOTION_ORDER[(i + 1) % MOTION_ORDER.length]);
        }}
        style={{
          padding: "5px 12px", borderRadius: 4, cursor: "pointer",
          background: "rgba(7,4,16,0.75)",
          border: `1px solid ${tone}55`,
          color: tone, fontFamily: GM.font, fontSize: 10,
          fontWeight: 900, letterSpacing: 1, whiteSpace: "nowrap",
        }}>
        ✦ MOTION · {(MOTION_LABELS[motionTier] || "Full").toUpperCase()}
      </button>
    );
  };

  const camBtn = (mode, label) => (
    <button key={mode} onClick={() => setCam((c) => ({ mode, n: c.n + 1 }))} style={{
      padding: "5px 12px", borderRadius: 4, cursor: "pointer",
      background: cam.mode === mode
        ? `linear-gradient(135deg, ${GM.inkGold}33, rgba(7,4,16,0.9))`
        : "rgba(7,4,16,0.75)",
      border: `1px solid ${cam.mode === mode ? GM.inkGold : GM.inkGold + "33"}`,
      color: cam.mode === mode ? GM.inkGoldBright : GM.vellumDim,
      fontFamily: GM.font, fontSize: 10, letterSpacing: 1.5,
      backdropFilter: "blur(8px)",
    }}>{label}</button>
  );

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {/* ⬡ SHIP 7 — THE TRANSPARENT CANVAS. This is the single largest
          change in the ship and it is one deleted line. <color
          attach="background"> painted an opaque void over the top of
          <ShatterfieldVeilBG> — the Rent Sun, the drifting obsidian
          shards, the warm/cold aenergy ribbons, and the entire SOLREND
          MIRROR (the sun's wound-cracks, one per ~5 Anima lost). All of
          it was already mounted behind the board in BOTH modes at z 0;
          3D was simply covering it up, which is most of why 3D read
          emptier than 2D. Drop the background, let the alpha through,
          and the 3D board is now standing INSIDE the 2D sky — which
          keeps reacting to the duel for free, in a file we never
          touched. Fog pushed out to 20→44 so the horizon curtains at
          r≈13 aren't eaten before they're seen. */}
      <Canvas shadows dpr={[1, 2]} camera={{ position: CAM.table.pos, fov: 46 }}
        gl={{ alpha: true, antialias: true }}
        onPointerMissed={() => { document.body.style.cursor = "default"; }}>
        <CssSync />
        <FrameWatch />
        <HoverReportCtx.Provider value={onCardHover}>
        <fog attach="fog" args={[SD.void1 || "#03020c", 20, 44]} />

        {/* Light rig — warm lamp over the field, violet rim (IE recipe) */}
        <ambientLight intensity={0.55} />
        <hemisphereLight args={["#3a2f55", "#0d0a28", 0.5]} />
        <directionalLight position={[6, 12, 8]} intensity={1.1} castShadow
          shadow-mapSize-width={1024} shadow-mapSize-height={1024}
          shadow-camera-left={-10} shadow-camera-right={10}
          shadow-camera-top={10} shadow-camera-bottom={-10}
          shadow-camera-far={40} />
        <pointLight position={[0, 6.5, 0]} intensity={26} distance={22}
          decay={1.6} color="#ffd9a0" />
        <directionalLight position={[-8, 6, -8]} intensity={0.5} color="#8a70ff" />

        {/* ⬡ SHIP 6 — EVERYTHING THAT IS THE DUEL LIVES INSIDE THE SHAKE.
            The lens never moves (CameraRig and its OrbitControls stay
            outside): the WORLD takes the hit. CSS3D faces copy their
            host mesh's matrixWorld, so the real DOM card faces ride it
            too, for free. */}
        <WorldShake>
          <FieldTable myTurn={myTurn} />
          <TurnSweep />
          <Motes />

          {/* ⬡ SHIP 7 — THE WEATHER. Inside the shake on purpose: when
              the world takes a hit, the mist and the rivers take it too.
              Every child under here is memo'd on a stable boolean, so
              these seven numbers changing reconciles NOTHING beneath. */}
          <FieldAtmos3D
            myAnima={me?.anima ?? 30} oppAnima={opponent?.anima ?? 30}
            maxAnima={me?.maxAnima ?? 30}
            myKhao={me?.khao ?? 0} myMaxKhao={me?.maxKhao ?? 3}
            oppKhao={opponent?.khao ?? 0} oppMaxKhao={opponent?.maxKhao ?? 3}
            myTurn={myTurn} />

          {/* MY seat */}
          <SeatSide player={me} isMe myTurn={myTurn} canDraw={canDraw}
            targetMode={targetMode} attackingSlot={attackingSlot}
            otherField={opponent?.field || null} effLyt={effLyt} effLyf={effLyf}
            seatKey="m" suppressed={suppressed} fxShakes={fx.shakes} fxPulses={fx.pulses}
            fxStrikes={fx.strikes} fxImpacts={fx.impacts}
            holoOf={holoOf} dropHint={dropHint}
            stampsFor={stampsFor} onSealPress={spawnSealRing}
            luxPulse={drawCue?.lux?.base || null} grimPulse={drawCue?.grim?.base || null}
            luxTone={drawCue?.luxTone?.base || null} grimTone={drawCue?.grimTone?.base || null}
            onSlot={onMySlot} onTome={onMyTome} onNecropolis={onMyNecropolis}
            onExile={onMyExile}
            onDrawGrimoire={onDrawGrimoire} onDrawLuxmoire={onDrawLuxmoire} />
          <MyHand hand={hand} selectedHandIdx={selectedHandIdx} suppressed={suppressed}
            holoOf={holoOf}
            onHandCardClick={onHandCardClick || (() => {})} />

          {/* ⬡ SHIP 7.5 — your companion, at your elbow */}
          <Companion3D myAnima={me?.anima ?? 30} oppAnima={opponent?.anima ?? 30} />

          {/* THEIR seat — π about Y: one layout, two chairs */}
          <group rotation={[0, Math.PI, 0]}>
            <SeatSide player={opponent} isMe={false} myTurn={myTurn}
              targetMode={targetMode} attackingSlot={null}
              otherField={me?.field || null} effLyt={effLyt} effLyf={effLyf}
              seatKey="o" suppressed={suppressed} fxShakes={fx.shakes} fxPulses={fx.pulses}
              fxStrikes={fx.strikes} fxImpacts={fx.impacts}
              onSlot={onOppSlot} onTome={onOppTome} onNecropolis={onOppNecropolis} />
            <OppHand count={(opponent?.hand || []).length} airborneTail={airborneOppDraws} />
          </group>

          {/* SHIP 3 — airborne cards + rising numbers (world space) */}
          {flights.map((f) => <FlightCard key={f.id} flight={f} onDone={handleFlightDone} />)}
          {fx.floats.map((f) => <FloatNumber key={f.id} float={f} />)}

          {/* SHIP 6 — arrival stamps and death shards */}
          {rings.map((r) => <LandingRing key={r.id} ring={r} onDone={dropRing} />)}
          {bursts.map((b) => <Shatter key={b.id} burst={b} onDone={dropBurst} />)}
          {/* H21 — the school marks, blooming where their cards landed */}
          {sigils.map((g) => <ArrivalSigil key={g.id} sigil={g} />)}
          {/* SHIP 8.2 — the ghosts: a shed form flashing out, a ruined
              body streaking into its grave. World-space, self-retiring. */}
          {ghosts.map((g) => (
            <GhostBody key={g.id} cardId={g.cardId} from={g.from} to={g.to} yaw={g.yaw}
                       kind={g.kind} onDone={() => dropGhost(g.id)} />
          ))}
          {/* SHIP 8.5 — the death marks rising out of the cellar */}
          {marks.map((mk) => (
            <DeathMark key={mk.id} from={mk.from} level={mk.level} onDone={() => dropMark(mk.id)} />
          ))}

          {/* ⬡ H23 — THE BESTIARY, PLAYING. Every live cue from every
              score currently running: the Pounce's claws, the Swarm's
              column, the Rite of Return's headstones. Inside the shake
              with everything else that is the duel, so a Calamity's
              rings ride the Calamity's own thunder. The layer is dumb
              by design — it does not know what a Spectral Tiger is, it
              plays cues and retires them. */}
          <ScoreLayer cues={cues} onCueDone={dropCue} />
        </WorldShake>

        <CameraRig cam={cam} />
        </HoverReportCtx.Provider>
      </Canvas>

      {/* ⬡ THE FACE LAYER — CSS3DRenderer's overlay. Every DOM card face
          lives in here, matrix3d'd into the card plane, above the WebGL
          canvas and below the chrome (chrome carries zIndex 20). Pointer
          events pass through so WebGL picking is untouched. */}
      <div ref={cssHostRef} style={{
        position: "absolute", inset: 0, overflow: "hidden",
        pointerEvents: "none", zIndex: 5,
      }} />
      {/* Plain-React face content — portals CardFaces into the CSS3D
          elements above. MUST live out here: DOM renders DOM. */}
      <FaceLayer />

      {/* ⬡ SHIP 6 — THE CHROME. The real duel HUD, floated over the
          board: Anima heart, Khao vortex, ornate name plates, framed
          timers, LUX/GRIM tiles, the danger veil and the strike flash.
          Pure DOM at z 12 — above the CSS3D faces (z 5), below
          Shatterfield.js's radials, heralds and modals (z 30+), which
          keep winning exactly as they do over the 2D board. */}
      <FieldChrome3D
        me={me} opponent={opponent} myTurn={myTurn} sharedTimer={sharedTimer} />

      {/* Camera chrome — DOM, GM palette, outside the world. Laid out
          as a ROW along the bottom edge now: the Anima heart owns the
          lower-left column (2D parity) and a vertical stack there would
          have sat on top of it. */}
      <div style={{
        position: "absolute", left: 12, bottom: 12, zIndex: 20,
        display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 6,
        maxWidth: 280,
      }}>
        {camBtn("table", "◈ TABLE")}
        {camBtn("mine", "▾ MY FIELD")}
        {camBtn("theirs", "▴ THEIR FIELD")}
        {camBtn("duel", "⚔ DUEL")}
        {/* ── T17 — THE ANIMATION DIAL ────────────────────────────
            caP'n's own guardrail on H21: "don't want people wasting
            their turn watching animations xD." FULL → QUICK → OFF on
            tap, persisted, and it lives HERE rather than three screens
            away in Plaza settings on purpose — the one moment a player
            decides animations are too slow is the moment they are
            watching one, and the fix should be under their thumb, not
            behind a menu walk. (V'ael's call, reversible: a Plaza
            Settings → Motion entry reading the same store is the
            obvious companion whenever that wave opens.) */}
        {motionBtn()}
      </div>
    </div>
  );
});
